import { Component, OnInit, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CategoryService } from '../../services/categories/categories.service';
import { Category, UnitType } from '../../services/categories/interfaces/categories.interface';
import { CommonModule } from '@angular/common';
import { MenuProduct } from '../../services/menu-product/interface/menu-product.interface';
import { MenuProductService } from '../../services/menu-product/menu-product.service';
import { SavedGroupsService, SavedGroupDTO } from '../../services/orders/saved-groups.service';
import { GroupAssignDialogComponent, GroupAssignDialogResult } from './group-assign-dialog.component';
import { MenuProductGroup } from '../../models/menu-product.model';
import { ItemOrderComponent } from '../../elements/order/item-order/item-order.component';
import { ItemOrder, Order } from '../../services/orders/interfaces/order.interface';
import { BehaviorSubject, catchError, forkJoin, map, merge, Observable, of, switchMap } from 'rxjs';
import { OrderService } from '../../services/orders/order.service';
import { TableInfo } from '../../services/tables/interfaces/table.interface';
import { TablesService } from '../../services/tables/tables.service';
import { OrderDetailService } from '../../services/orders/order-detail.service';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormGroup, FormBuilder, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { SharedOrderService } from '../../services/orders/shared-order.service';
import { MatDialog, MatDialogRef, MatDialogActions, MatDialogTitle, MatDialogContent, MatDialogClose, } from '@angular/material/dialog';
import { ClientNameDialogComponent } from '../../shared/client-name-dialog/client-name-dialog.component';
import { GroupEditDialogComponent, GroupEditDialogData, GroupEditDialogResult, GroupEditItemView } from './group-edit-dialog.component';
import { Location } from '@angular/common';
import { PaymentService } from '../../services/payment/payment.service';
import printJS from 'print-js';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { OpsToolbarComponent } from '../../ui/atoms/ops-toolbar/ops-toolbar.component';
import { ComboboxComponent, ComboOption } from '../../ui/atoms/combobox/combobox.component';
import { CheckboxComponent } from '../../ui/atoms/checkbox/checkbox.component';
import { KeycloakService } from 'keycloak-angular';
import { LocalStorageService } from '../../services/storage.service';
import { ConfigService } from '../../services/config.service';
import { BusinessContextService } from '../../services/business-context.service';
import { PrintersService } from '../../services/printers/printers.service';


@Component({
  selector: 'app-orders-new',
  standalone: true,
  imports: [
    CommonModule,
    ItemOrderComponent,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    LoadingSpinnerComponent,
    ComboboxComponent,
    MatSlideToggleModule,
    FormsModule
  ],
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.css'
})
export class OrdersComponent implements OnInit {
  // References to catalog category headers for scrolling
  @ViewChildren('catHeader') private catHeaders!: QueryList<ElementRef<HTMLElement>>;
  breadcrumbList = {
    title: 'Lest Go!',
    subTitle: 'Dashboard',
    menu_path: 'Dashboard',
    currentURL: 'New Order',
  }

  // Helper: items that count for totals (exclude canceled)
  private getNonCanceledItems(list: ItemOrder[]): ItemOrder[] {
    if (!Array.isArray(list)) return [];
    return list.filter(it => ((it.itemStatusSlug || '').toLowerCase() !== 'canceled'));
  }

  // Helper: recompute order totals excluding canceled items
  private recomputeTotals(): void {
    try {
      const effective = this.getNonCanceledItems(this.orderList);
      this.order.totalAmount = effective.reduce((acc: number, e) => acc + (Number(e.qty) * Number(e.price)), 0);
    } catch {
      this.order.totalAmount = 0;
    }
  }

  // Delete when empty; close when all items are canceled
  async onDeleteOrderOrClose() {
    try {
      if (!this.orderHashId) return;
      const isEmpty = !Array.isArray(this.orderList) || this.orderList.length === 0;
      if (isEmpty) {
        // Confirm and delete
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: '320px',
          data: { itemName: `Eliminar la orden vacía ${this.orderHashId.slice(-6)}?` }
        });
        const result = await dialogRef.afterClosed().toPromise();
        if (!result) return;
        this.isLoading = true;
        await this.orderService.deleteOrder(this.orderHashId).toPromise();
        this.router.navigate(['/admin/order-list']);
        return;
      }

      if (this.areAllItemsCanceled()) {
        // Confirm and close (do not delete)
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: '320px',
          data: { itemName: `Cerrar la orden (todos los ítems cancelados)` }
        });
        const result = await dialogRef.afterClosed().toPromise();
        if (!result) return;
        this.isLoading = true;
        await this.orderService.closeOrder(this.orderHashId).toPromise();
        await this.loadOrderDetails();
        return;
      }
    } catch (e) {
      console.error('onDeleteOrderOrClose error', e);
    } finally {
      this.isLoading = false;
    }
  }



  // Robust parser for creation timestamp
  private parseCreated(val: any): number {
    if (val instanceof Date) return val.getTime();
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const t = Date.parse(val);
      if (!isNaN(t)) return t;
    }
    return Number.MAX_SAFE_INTEGER; // push unknowns to the end
  }

  // Order items by creation time (oldest first), fallback by uuid
  private sortOrderListByCreated() {
    if (!Array.isArray(this.orderList)) return;
    this.orderList.sort((a: ItemOrder, b: ItemOrder) => {
      const ta = this.parseCreated((a as any).createdAt);
      const tb = this.parseCreated((b as any).createdAt);
      if (ta !== tb) return ta - tb;
      const ua = (a?.uuid || '').toString();
      const ub = (b?.uuid || '').toString();
      return ua.localeCompare(ub);
    });
  }

  // ===== Catalog quick add/remove by menuProductUuid =====
  getQtyForMenuProduct(menuProductUuid: string): number {
    if (!menuProductUuid) return 0;
    return this.orderList
      .filter(it => (it.menuProductUuid === menuProductUuid))
      .reduce((sum, it) => sum + (Number(it.qty) || 0), 0);
  }

  // Total de items seleccionados para un MenuProduct (considera opciones)
  getTotalItemsForMenuProduct(menuProductUuid: string): number {
    if (!menuProductUuid) return 0;
    // Preferir totales provenientes del backend en order.menuProducts
    try {
      const mpGroups = Array.isArray((this.order as any)?.menuProducts) ? (this.order as any).menuProducts : [];
      const found = mpGroups.find((grp: any) => (grp?.menuProduct?.uuid || grp?.menuProduct?.id) === menuProductUuid);
      const backendTotal = Number(found?.menuProduct?.totalItems ?? 0);
      if (!isNaN(backendTotal) && backendTotal > 0) return backendTotal;
    } catch { }
    const items = this.orderList.filter(it => it.menuProductUuid === menuProductUuid);
    const countUnits = (it: any): number => {
      const opts = Array.isArray(it?.options) ? it.options : [];
      const snaps = Array.isArray(it?.optionSnapshots) ? it.optionSnapshots : [];
      if (opts.length > 0) return opts.length;
      if (snaps.length > 0) return snaps.length;
      if (it?.menuProductOption || it?.menuProductOptionUuid) {
        const q = Number(it?.qty || 1);
        return q > 0 ? q : 1;
      }
      const q = Number(it?.qty || 0);
      return q > 0 ? q : 0;
    };
    return items.reduce((sum, it) => sum + countUnits(it), 0);
  }

  // Total de venta para un MenuProduct (precio * totalItems)
  getTotalSellPriceForMenuProduct(menuProductUuid: string): number {
    if (!menuProductUuid) return 0;
    // Preferir totalSellPrice del backend en order.menuProducts
    try {
      const mpGroups = Array.isArray((this.order as any)?.menuProducts) ? (this.order as any).menuProducts : [];
      const found = mpGroups.find((grp: any) => (grp?.menuProduct?.uuid || grp?.menuProduct?.id) === menuProductUuid);
      const backendTotal = Number(found?.menuProduct?.totalSellPrice ?? 0);
      if (!isNaN(backendTotal) && backendTotal > 0) return backendTotal;
    } catch { }
    const items = this.orderList.filter(it => it.menuProductUuid === menuProductUuid);
    const countUnits = (it: any): number => {
      const opts = Array.isArray(it?.options) ? it.options : [];
      const snaps = Array.isArray(it?.optionSnapshots) ? it.optionSnapshots : [];
      if (opts.length > 0) return opts.length;
      if (snaps.length > 0) return snaps.length;
      if (it?.menuProductOption || it?.menuProductOptionUuid) {
        const q = Number(it?.qty || 1);
        return q > 0 ? q : 1;
      }
      const q = Number(it?.qty || 0);
      return q > 0 ? q : 0;
    };
    const unitTotal = items.reduce((sum, it: any) => {
      const unitPrice = Number(it?.price ?? it?.menuProduct?.sellPrice ?? 0) || 0;
      return sum + unitPrice * countUnits(it);
    }, 0);
    return unitTotal;
  }

  async addMenuProduct(menuProductUuid: string) {
    if (this.isOrderClosed) { console.warn('Order is closed. No actions allowed.'); return; }
    if (!menuProductUuid) return;
    try {
      this.isLoading = true;
      // Prefer increasing an existing PENDING line if present
      const existingPending = this.orderList.find(it => it.menuProductUuid === menuProductUuid && (it.itemStatusSlug || 'pending').toLowerCase() === 'pending');
      if (existingPending) {
        const updated: any = await this.orderDetailService.updateOrderDetail({
          orderUuid: existingPending.orderUuid || (await this.ensureOrderUuid()),
          uuid: existingPending.uuid,
          qty: (existingPending.qty || 0) + 1,
        }).toPromise();
        existingPending.qty = updated?.qty ?? (existingPending.qty + 1);
        this.recomputeTotals();
        // mirror in order.orderDetail
        const odIndex = Array.isArray(this.order?.orderDetail) ? this.order.orderDetail.findIndex((od: any) => od.uuid === existingPending.uuid) : -1;
        if (odIndex > -1) this.order.orderDetail[odIndex].qty = existingPending.qty;
      } else {
        // Create a new pending line for this product
        // Prefer buscar en el catálogo agrupado por categoría (catalogGroups)
        const catalogSafe = Array.isArray(this.catalogGroups) ? this.catalogGroups : [];
        const found = catalogSafe
          .flatMap(g => Array.isArray(g?.items) ? g.items : [])
          .find((mp: any) => (mp?.uuid || mp?.id) === menuProductUuid) as any;
        const orderUuidToUse = await this.ensureOrderUuid();
        const created: any = await this.orderDetailService.createOrderDetail({
          orderUuid: orderUuidToUse,
          menuProductUuid,
          qty: 1,
        }).toPromise();
        const newItem = new ItemOrder();
        newItem.qty = created?.qty ?? 1;
        newItem.menuProductUuid = menuProductUuid;
        newItem.name = found?.name || 'Producto';
        newItem.price = Number(found?.sellPrice ?? 0);
        newItem.description = found?.description || '';
        newItem.orderUuid = orderUuidToUse;
        newItem.uuid = created?.uuid || '';
        newItem.itemStatusSlug = (created?.itemStatusSlug || 'pending');
        newItem.createdAt = created?.createdAt || created?.created_at || created?.created || Date.now();
        this.orderList.push(newItem);
        if (!Array.isArray(this.order.orderDetail)) this.order.orderDetail = [];
        this.order.orderDetail.push(created);
        this.recomputeTotals();
      }
      this.sortOrderListByCreated();
      this.updateFlowFlagsAndPayment();
    } catch (error) {
      console.error('addMenuProduct error', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async ensureOrderUuid(): Promise<string> {
    // If we already have an id, return it
    if (this.orderHashId && this.orderHashId.length > 0) return this.orderHashId;
    // Try to infer from loaded order object
    const candidate = (
      (this.order as any)?.uuid
      || (this.order as any)?.hash
      || (this.order as any)?.id
      || (this.order as any)?.orderUuid
      || (this.order as any)?.hashId
    );
    if (candidate) {
      this.orderHashId = String(candidate);
      return this.orderHashId;
    }
    // As a last resort, create the order now (only in orders-new flow)
    const createdResp: any = await this.orderService.createOrder({ tableId: this.tableId, clientName: this.customerName }).toPromise();
    const apiOrder = createdResp && typeof createdResp === 'object' && (createdResp as any).order
      ? (createdResp as any).order
      : createdResp;
    this.order = apiOrder;
    const idCandidate = (
      (apiOrder as any)?.uuid
      || (apiOrder as any)?.hash
      || (apiOrder as any)?.id
      || (apiOrder as any)?.orderUuid
      || (apiOrder as any)?.hashId
    );
    this.orderHashId = idCandidate ? String(idCandidate) : '';
    return this.orderHashId;
  }

  async removeMenuProduct(menuProductUuid: string) {
    if (this.isOrderClosed) { console.warn('Order is closed. No actions allowed.'); return; }
    if (!menuProductUuid) return;
    // Choose a line to decrement: prefer a pending line first, otherwise any line with qty>0
    const candidate = this.orderList.find(it => it.menuProductUuid === menuProductUuid && (it.itemStatusSlug || 'pending').toLowerCase() === 'pending')
      || this.orderList.find(it => it.menuProductUuid === menuProductUuid && (Number(it.qty) || 0) > 0);
    if (!candidate) return;
    await this.onRemoveItem(candidate.uuid);
  }

  // Apply discount to backend and refresh
  async onApplyDiscount() {
    try {
      if (!this.orderHashId) return;
      // Backend exige entero y no negativo
      const amount = Math.max(0, Math.floor(Number(this.discountAmount || 0)));
      const note = (this.discountNote || '').toString();
      this.isLoading = true;
      await this.orderService.setOrderDiscount(this.orderHashId, amount, note).toPromise();
      await this.loadOrderDetails();
    } catch (e) {
      console.error('Error applying discount', e);
    } finally {
      this.isLoading = false;
    }
  }

  // Coaccionar el input del descuento a entero >= 0
  onDiscountAmountChange(val: any): void {
    this.discountAmount = Math.max(0, Math.floor(Number(val || 0)));
  }

  // Aplicar delivery (local, opcional)
  async onApplyDelivery(): Promise<void> {
    try {
      if (!this.orderHashId) return;
      const amount = Math.max(0, Math.floor(Number(this.deliveryAmount || 0)));
      const note = (this.deliveryNote || '').toString();
      this.deliveryAmount = amount;
      this.deliveryNote = note;
      this.isLoading = true;
      await this.orderService.setOrderDelivery(this.orderHashId, amount).toPromise();
      await this.loadOrderDetails();
    } catch (e) {
      console.error('Error applying delivery', e);
    } finally {
      this.isLoading = false;
    }
  }

  // Coaccionar el input del delivery a entero >= 0
  onDeliveryAmountChange(val: any): void {
    this.deliveryAmount = Math.max(0, Math.floor(Number(val || 0)));
  }

  // Editar nombre del cliente desde el encabezado
  async onEditClientName() {
    try {
      const currentName = this.normalizeClientName(this.customerName || '');
      const initialCreated = (
        (this.order?.createdAt)
        || (this.order?.created_at)
        || (this.order?.createdDate)
        || undefined
      );
      const result = await this.openClientNameDialog(currentName);
      if (!result) return;
      const nextName = this.normalizeClientName(result.name || '');

      // Persist name change if any
      if (nextName !== currentName) {
        if (!this.orderHashId) {
          console.warn('No hay identificador de orden para actualizar el cliente');
        } else {
          this.isLoading = true;
          await this.orderService.setOrderClientName(this.orderHashId, nextName).toPromise();
          this.customerName = nextName;
          if (this.order) (this.order as any).customerName = nextName;
        }
      }
    } catch (e) {
      console.error('Error actualizando nombre de la orden', e);
    } finally {
      this.isLoading = false;
    }
  }

  // Computed helper for template clarity
  get isOrderOpen(): boolean {
    const s = (this.statusOrder || '').toLowerCase();
    return s.includes('abiert') || (!this.isOrderClosed && s === 'open');
  }

  // True when the order has a status equivalent to PAYMENT_PENDING ("Por pagar")
  get isOrderPaymentPending(): boolean {
    try {
      const norm = this.normalizeStatus(this.order?.status);
      return (norm?.slug || '') === 'PAYMENT_PENDING';
    } catch { return false; }
  }

  // Consider the order effectively unpaid when all recorded base amounts are 0
  // Checks both consolidated payment header and individual payment lines
  get isOrderEffectivelyUnpaid(): boolean {
    try {
      const pay: any = (this.order as any)?.payment || {};
      const payments: any[] = Array.isArray((this.order as any)?.payments) ? (this.order as any).payments : [];
      const baseFromHeader = (Number(pay?.cardAmount ?? 0) || 0) + (Number(pay?.cashAmount ?? 0) || 0);
      const baseFromLines = payments.reduce((acc: number, p: any) => {
        const card = Number(p?.cardAmount ?? 0) || 0;
        const cash = Number(p?.cashAmount ?? 0) || 0;
        return acc + card + cash;
      }, 0);
      const totalBase = baseFromHeader + baseFromLines;
      return totalBase <= 0;
    } catch { return false; }
  }

  // Whether discount fields can be edited
  canEditDiscount(): boolean {
    // Permitir editar descuento mientras la orden no esté cerrada
    return !this.isOrderClosed;
  }

  // Solo las mesas de tipo DELIVERY deben mostrar campo Delivery
  get isDeliveryTable(): boolean {
    const t = (this.table?.type || '').toString().toUpperCase();
    return t === 'DELIVERY';
  }

  // Permitir editar delivery cuando la orden no esté cerrada y sea mesa DELIVERY
  canEditDelivery(): boolean {
    return !this.isOrderClosed && this.isDeliveryTable;
  }

  // Computed total considering discount
  get totalAfterDiscount(): number {
    // Preferir el total proveniente del backend
    const pay: any = (this.order as any)?.payment || {};
    const hasTotalDue = pay && pay.totalDue !== undefined && pay.totalDue !== null && !isNaN(Number(pay.totalDue));
    if (hasTotalDue) return Number(pay.totalDue);
    // Si no viene totalDue, calcular con itemsSubtotal y discount si existen
    const hasItemsSubtotal = pay && pay.itemsSubtotal !== undefined && !isNaN(Number(pay.itemsSubtotal));
    const hasDiscount = pay && pay.discount !== undefined && !isNaN(Number(pay.discount));
    if (hasItemsSubtotal && hasDiscount) {
      return Number(pay.itemsSubtotal) - Number(pay.discount);
    }
    // Fallback: usar totalAmount y discountAmount locales
    const total = Number(this.order?.totalAmount) || 0;
    const discount = Math.max(0, Math.floor(Number(this.discountAmount || 0)));
    return total - discount;
  }

  // Suggested tip 10% over discounted total
  get suggestedTip(): number {
    // Preferir el tip del backend si estuviera definido
    const pay: any = (this.order as any)?.payment || {};
    const hasTip = pay && pay.tip !== undefined && pay.tip !== null && !isNaN(Number(pay.tip));
    if (hasTip) return Math.round(Number(pay.tip));
    // No sugerir propina si el total después de descuento es negativo
    return Math.round((Math.max(0, this.totalAfterDiscount) || 0) * 10 / 100);
  }

  // Final total including suggested tip
  get finalTotal(): number {
    // Preferir total del backend si viene separado: totalDue + tip
    const pay: any = (this.order as any)?.payment || {};
    const hasTotalDue = pay && pay.totalDue !== undefined && pay.totalDue !== null && !isNaN(Number(pay.totalDue));
    const hasTip = pay && pay.tip !== undefined && pay.tip !== null && !isNaN(Number(pay.tip));
    if (hasTotalDue || hasTip) {
      return (Number(pay.totalDue || 0)) + (Number(pay.tip || 0));
    }
    // El total puede ser negativo; la propina sugerida será 0 en ese caso
    const deliveryInt = Math.max(0, Math.floor(Number(this.deliveryAmount || 0)));
    return (this.totalAfterDiscount || 0) + deliveryInt + (this.suggestedTip || 0);
  }

  async onDeleteOrder() {
    try {
      if (!this.orderHashId) {
        console.warn('No hay identificador de orden para eliminar.');
        return;
      }
      // Confirm dialog reuse
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '320px',
        data: { itemName: `Eliminar la orden ${this.orderHashId.slice(-6)}?` }
      });
      const result = await dialogRef.afterClosed().toPromise();
      if (!result) return;

      this.isLoading = true;
      await this.orderService.deleteOrder(this.orderHashId).toPromise();
      // Al eliminar, volvemos a la lista
      this.router.navigate(['/admin/order-list']);
    } catch (error) {
      console.error('Error al eliminar la orden:', error);
    } finally {
      this.isLoading = false;
    }
  }

  // Normalize various backend status shapes into a consistent slug and display
  private normalizeStatus(raw: any): { slug: 'OPEN' | 'CLOSED' | 'PENDING' | 'PAYMENT_PENDING' | string, display: string } {
    // raw may be: string ("cerrada"), or object with fields like name/description/d/slug
    let slug = '';
    let display = '';
    if (raw && typeof raw === 'object') {
      const name = (raw.name || raw.statusName || raw.description || '').toString();
      const d = (raw.d || raw.slug || raw.statusSlug || name).toString();
      display = name || d;
      slug = d.toString().toUpperCase().trim();
    } else if (typeof raw === 'string') {
      display = raw;
      slug = raw.toUpperCase().trim();
    } else {
      slug = 'PENDING';
      display = 'Pendiente';
    }
    // Map common spanish variants
    const map: Record<string, string> = {
      'CERRADA': 'CLOSED',
      'CERRADO': 'CLOSED',
      'CLOSED': 'CLOSED',
      'ABIERTA': 'OPEN',
      'ABIERTO': 'OPEN',
      'OPEN': 'OPEN',
      'POR PAGAR': 'PAYMENT_PENDING',
      'POR-PAGAR': 'PAYMENT_PENDING',
      'POR_PAGAR': 'PAYMENT_PENDING',
      'PAYMENT_PENDING': 'PAYMENT_PENDING'
    };
    const normalizedSlug = map[slug] || slug;
    return { slug: normalizedSlug as any, display };
  }

  // Keep items stably ordered by UUID ascending
  private sortOrderListByUuid() {
    if (!Array.isArray(this.orderList)) return;
    this.orderList.sort((a: ItemOrder, b: ItemOrder) => {
      const ua = (a?.uuid || '').toString();
      const ub = (b?.uuid || '').toString();
      const aEmpty = ua.length === 0;
      const bEmpty = ub.length === 0;
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;   // elementos sin UUID al final
      if (bEmpty) return -1;  // elementos sin UUID al final
      return ua.localeCompare(ub);
    });
  }

  // Template-safe helper: returns true if any item can advance
  canAdvanceAll(): boolean {
    return this.orderList.some(it => (it.allowedNextStatuses?.length || 0) > 0);
  }

  // Only allow deleting an order when it's open and all items are still pending
  canDeleteOrder(): boolean {
    if (!this.isOrderOpen) return false;
    if (!Array.isArray(this.orderList) || this.orderList.length === 0) return false;
    return this.orderList.every(it => (it.itemStatusSlug || '').toLowerCase() === 'pending');
  }

  // True when every item in the order is canceled
  private areAllItemsCanceled(): boolean {
    return Array.isArray(this.orderList)
      && this.orderList.length > 0
      && this.orderList.every(it => (it.itemStatusSlug || '').toLowerCase() === 'canceled');
  }

  // Show delete button when order is open and either empty or all items canceled
  canDeleteOrCloseOrder(): boolean {
    const isEmpty = !Array.isArray(this.orderList) || this.orderList.length === 0;
    if (this.isOrderOpen && isEmpty) return true;
    const allCanceled = this.areAllItemsCanceled();
    const unpaid = this.isOrderEffectivelyUnpaid;
    return allCanceled && unpaid;
  }

  async onDeleteOrderWithWarning() {
    try {
      if (!this.orderHashId) {
        console.warn('No hay identificador de orden para eliminar.');
        return;
      }
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '320px',
        data: { itemName: `Esta acción eliminará la orden ${this.orderHashId.slice(-6)}.\n\nLa orden cumple condiciones de borrado (ítems cancelados y pago no realizado, o vacía). ¿Confirmas eliminarla?` }
      });
      const result = await dialogRef.afterClosed().toPromise();
      if (!result) return;
      this.isLoading = true;
      await this.orderService.deleteOrder(this.orderHashId).toPromise();
      this.router.navigate(['/admin/order-list']);
    } catch (error) {
      console.error('Error al eliminar la orden:', error);
    } finally {
      this.isLoading = false;
    }
  }

  // Advance all items to their primary next status (prefer non-canceled)
  async onAdvanceAllPrimary() {
    if (this.isOrderClosed) { console.warn('Order is closed. No actions allowed.'); return; }
    const toUpdates = this.orderList
      .map(it => {
        const list = it.allowedNextStatuses || [];
        if (!list.length) return null as unknown as { item: ItemOrder, next: string };
        const preferred = list.find(s => s !== 'canceled');
        const next = preferred || list[0];
        return { item: it, next };
      })
      .filter(Boolean) as Array<{ item: ItemOrder; next: string }>;

    if (toUpdates.length === 0) return;

    try {
      this.isLoading = true;

      // Group items by the next status
      const itemsByStatus = toUpdates.reduce((acc, { item, next }) => {
        if (!acc[next]) acc[next] = [];
        acc[next].push(item.uuid);
        return acc;
      }, {} as Record<string, string[]>);

      // Process each status update
      const orderUuid = this.orderList[0]?.orderUuid;
      if (!orderUuid) return;

      // Process all status updates sequentially
      for (const [action, itemUuids] of Object.entries(itemsByStatus)) {
        await this.orderDetailService.processItems(
          orderUuid,
          itemUuids,
          action,
          true // onlyPending
        ).toPromise();
      }

      // Refresh the order to get the latest status
      await this.loadOrderDetails();

    } catch (error) {
      console.error('Error advancing items:', error);
      // Even if there's an error, try to refresh the order to get the latest state
      try {
        await this.loadOrderDetails();
      } catch (refreshError) {
        console.error('Error refreshing order:', refreshError);
      }
    } finally {
      this.isLoading = false;
    }
  }

  // Advance a single item's status to a specific next status
  async onChangeItemStatus(evt: { uuid: string; nextStatus: string }) {
    if (this.isOrderClosed) { console.warn('Order is closed. No actions allowed.'); return; }
    const { uuid, nextStatus } = evt || ({} as any);
    if (!uuid || !nextStatus) return;

    const itemOrder = this.orderList.find(i => i.uuid === uuid);
    if (!itemOrder) return;

    // Ensure transition is allowed
    const allowed = itemOrder.allowedNextStatuses || [];
    if (!allowed.includes(nextStatus)) return;

    try {
      this.isLoading = true;

      // Ensure orderUuid is a string
      const orderUuid = itemOrder.orderUuid || '';
      if (!orderUuid) {
        console.error('Order UUID is missing');
        return;
      }

      // Use the processItems endpoint to update the status
      await this.orderDetailService.processItems(
        orderUuid,
        [uuid],
        nextStatus,
        true // onlyPending
      ).toPromise();

      // Refresh the order to get the latest status
      await this.loadOrderDetails();

    } catch (error) {
      console.error('Error updating item status:', error);
      // Even if there's an error, try to refresh the order
      try {
        await this.loadOrderDetails();
      } catch (refreshError) {
        console.error('Error refreshing order:', refreshError);
      }
    } finally {
      this.isLoading = false;
    }
  }

  // Compute the set of bulk next statuses available at order level
  getOrderBulkNextStatuses(): string[] {
    const set = new Set<string>();
    this.orderList.forEach(it => (it.allowedNextStatuses || []).forEach(s => set.add(s)));
    return Array.from(set);
  }

  // Advance all items that allow the provided nextStatus
  async onAdvanceOrderStatus(nextStatus: string) {
    if (this.isOrderClosed) { console.warn('Order is closed. No actions allowed.'); return; }
    if (!nextStatus) return;
    const toUpdate = this.orderList.filter(it => (it.allowedNextStatuses || []).includes(nextStatus));
    if (toUpdate.length === 0) return;
    try {
      this.isLoading = true;
      await Promise.all(toUpdate.map(it => this.orderDetailService.updateOrderDetail({
        orderUuid: it.orderUuid,
        uuid: it.uuid,
        itemStatusSlug: nextStatus
      }).toPromise().then(() => {
        it.itemStatusSlug = nextStatus;
      })));
      this.updateFlowFlagsAndPayment();
    } catch (error) {
      console.error({ error });
    } finally {
      this.isLoading = false;
    }
  }

  async onCancelItem(itemUuid: string) {
    if (this.isOrderClosed) { console.warn('Order is closed. No actions allowed.'); return; }
    if (this.isOrderPaymentPending) { console.warn('Order is payment pending. No actions allowed.'); return; }
    const itemOrder = this.orderList.find(({ uuid }) => uuid === itemUuid);
    if (!itemOrder) {
      console.error(`Item with UUID ${itemUuid} not found in order`);
      return;
    }


    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '300px',
      data: { itemName: itemOrder.name }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) return; // User cancelled the dialog

    try {
      this.isLoading = true;
      const orderUuid = itemOrder.orderUuid || '';
      if (!orderUuid) {
        throw new Error(`No order UUID found for item ${itemUuid}`);
      }

      console.log(`Attempting to cancel item ${itemOrder.name} (${itemUuid}) from order ${orderUuid}`);

      await this.orderDetailService.processItems(
        orderUuid,
        [itemUuid],
        'canceled',
        true
      ).toPromise();

      console.log(`Successfully canceled item ${itemUuid}`);

      // Update local state
      itemOrder.itemStatusSlug = 'canceled';
      this.updateFlowFlagsAndPayment();

      // Refresh order details to ensure UI is in sync with backend
      await this.loadOrderDetails();
    } catch (error) {
      console.error(`Error canceling item ${itemOrder.name} (${itemUuid}):`, error);
      // Try to refresh the order to ensure UI is in sync
      try {
        await this.loadOrderDetails();
      } catch (refreshError) {
        console.error('Error refreshing order after cancellation error:', refreshError);
      }
    } finally {
      this.isLoading = false;
    }
  }

  private updateFlowFlagsAndPayment() {
    const type = (this.table?.type || 'LOCAL') as 'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK';
    const flows = this.itemStatusFlows?.[type] || this.DEFAULT_ITEM_STATUS_FLOWS[type] || {};
    this.orderList.forEach((it) => {
      const current = (it.itemStatusSlug || 'pending').toLowerCase();
      it.allowedNextStatuses = flows[current] || [];
      // Un ítem es editable solo si está en 'pending' y NO pertenece a un grupo pagado
      it.canEdit = current === 'pending' && !this.isItemInPaidGroup(it.uuid);
      // Habilitar borrar/cancelar item siempre que la orden NO esté cerrada ni en pago pendiente
      // y el ítem NO pertenezca a un grupo pagado
      const orderPaymentPending = this.isOrderPaymentPending;
      it.canCancel = !this.isOrderClosed && !orderPaymentPending && !this.isItemInPaidGroup(it.uuid);
    });
    // Payment enabling rules by order type
    // Nuevo requerimiento: el pago solo debe mostrarse/habilitarse cuando
    // todos los ítems estén en estado final (delivered o canceled),
    // independiente del tipo de orden.
    const isClosed = this.order?.status?.d === 'CLOSED';
    if (isClosed) {
      this.isPaymentDisabled = true;
      this.allItemsFinal = false;
      return;
    }

    // Compute allItemsFinal a partir de los estados finales definidos por flows (valores vacíos)
    const flowsForType = this.itemStatusFlows?.[type] || {};
    const finalStatuses = Object.entries(flowsForType)
      .filter(([_, next]) => Array.isArray(next) && next.length === 0)
      .map(([status]) => status.toLowerCase());
    // Asegurar incluir 'canceled' como final aunque no estuviera en flows
    if (!finalStatuses.includes('canceled')) finalStatuses.push('canceled');
    this.allItemsFinal = this.orderList.length > 0 && this.orderList.every(it => {
      const s = (it.itemStatusSlug || '').toLowerCase();
      return finalStatuses.includes(s);
    });

    // Para FOODTRUCK: el pago se habilita cuando todos los ítems activos están en 'pending'
    // (se paga antes de cocinar). Para el resto: al finalizar todos los ítems.
    if (type === 'FOODTRUCK') {
      const active = this.getNonCanceledItems(this.orderList);
      const allPending = active.length > 0 && active.every(it => (it.itemStatusSlug || 'pending').toLowerCase() === 'pending');
      this.isPaymentDisabled = !allPending;
    } else {
      this.isPaymentDisabled = !this.allItemsFinal;
    }

    // Recalcular opciones de grupo para pago parcial
    this.computeGroupOptions();
  }

  // Show Pay button
  canShowPay(): boolean {
    if (this.isOrderClosed) return false;
    const isFoodtruck = (this.table?.type || '').toString().toUpperCase() === 'FOODTRUCK';
    if (isFoodtruck) {
      // Para FOODTRUCK: mostrar pagar cuando todos los ítems activos están pendientes
      const active = this.getNonCanceledItems(this.orderList);
      return active.length > 0 && active.every(it => (it.itemStatusSlug || 'pending').toLowerCase() === 'pending');
    }
    return this.allItemsFinal;
  }

  // Show Voucher button
  canShowVoucher(): boolean {
    // Mostrar siempre el botón de voucher (la impresión es útil en cualquier estado)
    return true;
  }

  orderHashId: string = '';
  order: any = null;
  categories: Category[] = [];
  categories$ = new BehaviorSubject<Category[]>([]);
  orderList: ItemOrder[] = [];
  tableId: any;
  table: TableInfo = null as any;
  statusOrder: string = '';
  orderStatusSlug: string = '';
  isOrderClosed = false;
  isPaymentDisabled = true;
  allItemsFinal = false; // true when every item is delivered or canceled
  isLoading = true;
  customerName: string = '';
  // Discount state for voucher/ticket
  discountAmount: number = 0;      // valor absoluto a descontar
  discountNote: string = '';
  // Delivery opcional (solo mesas DELIVERY)
  deliveryAmount: number = 0;      // costo de delivery
  deliveryNote: string = '';
  // Default flows per table type (fallback if backend not available)
  // Business rule:
  //  - Client can move from 'pending' -> 'sent'.
  //  - Once 'sent', client cannot modify; Kitchen moves it to 'ready'.
  //  - From 'ready', client can advance to 'delivered'.
  private readonly DEFAULT_ITEM_STATUS_FLOWS: Record<'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK', Record<string, string[]>> = {
    LOCAL: {
      pending: ['sent', 'canceled'],
      sent: [],              // No client-side transitions from 'sent'
      ready: ['delivered'],  // Kitchen sets to 'ready'; client advances to 'delivered'
      delivered: [],
      canceled: [],
    },
    DELIVERY: {
      pending: ['sent', 'canceled'],
      sent: [],
      ready: ['delivered'],
      delivered: [],
      canceled: [],
    },
    PICKUP: {
      pending: ['sent', 'canceled'],
      sent: [],
      ready: ['picked'],     // For pickup, 'ready' -> 'picked' (collected)
      delivered: [],
      canceled: [],
      picked: []
    },
    FOODTRUCK: {
      pending: [],           // Payment triggers auto-advance to 'ready'
      sent: [],
      ready: ['delivered'],  // Single action: deliver to customer
      delivered: [],
      canceled: [],
    }
  } as const;
  // Flows loaded from backend (initialized with defaults)
  private itemStatusFlows: Record<'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK', Record<string, string[]>> = this.DEFAULT_ITEM_STATUS_FLOWS;

  constructor(private router: Router, private route: ActivatedRoute,
    private tableService: TablesService,
    private categoryService: CategoryService,
    private orderDetailService: OrderDetailService,
    private orderService: OrderService,
    private paymentService: PaymentService,
    public dialog: MatDialog,
    private location: Location,
    private sharedOrderService: SharedOrderService,
    private menuProductService: MenuProductService,
    private savedGroupsService: SavedGroupsService,
    private toast: NgxToastrService,
    private keycloak: KeycloakService,
    private localStorage: LocalStorageService,
    private config: ConfigService,
    private bizCtx: BusinessContextService,
    private printersService: PrintersService,
  ) { }

  // --- Accordion state for embedded catalog ---
  private openCategories = new Set<number>();
  isCategoryOpen(index: number): boolean { return this.openCategories.has(index); }
  toggleCategory(index: number): void {
    if (this.openCategories.has(index)) {
      // If the same category is open, close it (no category open)
      this.openCategories.delete(index);
    } else {
      // Open only this category and close all others
      this.openCategories.clear();
      this.openCategories.add(index);
      // After Angular renders, scroll the opened header into view
      setTimeout(() => this.scrollCategoryIntoView(index), 0);
    }
  }

  // Toggle entire Catalog block
  toggleCatalog(): void {
    this.isCatalogOpen = !this.isCatalogOpen;
  }

  // Smoothly scroll the category header into view, accounting for fixed header height
  private scrollCategoryIntoView(index: number): void {
    try {
      const el = this.catHeaders?.toArray()?.[index]?.nativeElement;
      if (!el) return;
      const headerOffset = 90; // approximate fixed header height
      const rect = el.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const top = rect.top + scrollTop - headerOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    } catch { }
  }

  // Helper: get a stable key for MenuProduct from either API shape
  getMpKey(mp: any): string {
    return (mp && (mp.uuid || mp.id)) || '';
  }

  // Catalog visibility helpers
  isProductDisabled(key: string): boolean { return !!key && this.disabledMenuProducts.has(key); }
  isProductDeleted(key: string): boolean { return !!key && this.deletedMenuProducts.has(key); }
  getProductEnabled(p: any): boolean {
    const key = this.getMpKey(p);
    const backendEnabled = (p?.enabled ?? p?.isEnabled);
    const enabled = (backendEnabled === undefined || backendEnabled === null) ? true : !!backendEnabled;
    if (this.isProductDeleted(key)) return false;
    if (this.isProductDisabled(key)) return false;
    return enabled;
  }
  shouldShowProduct(p: any): boolean {
    return this.getProductEnabled(p);
  }
  onToggleDisableProduct(key: string, checked: boolean): void {
    if (!key) return;
    if (checked) this.disabledMenuProducts.add(key); else this.disabledMenuProducts.delete(key);
    try { localStorage.setItem(this.DISABLED_STORAGE_KEY, JSON.stringify(Array.from(this.disabledMenuProducts))); } catch { }
  }
  async onToggleEnableProduct(p: any, checked: boolean) {
    const key = this.getMpKey(p);
    // Try backend first
    try {
      const svc: any = this.menuProductService as any;
      if (svc?.updateMenuProduct && typeof svc.updateMenuProduct === 'function') {
        await svc.updateMenuProduct(key, { enabled: checked }).toPromise?.() ?? svc.updateMenuProduct(key, { enabled: checked }).toPromise?.();
      } else if (svc?.setMenuProductEnabled) {
        await svc.setMenuProductEnabled(key, checked).toPromise?.() ?? svc.setMenuProductEnabled(key, checked).toPromise?.();
      } else {
        // Fallback: local only
        this.onToggleDisableProduct(key, !checked);
      }
    } catch (e) {
      console.warn('No se pudo actualizar enabled en backend, usando fallback local', e);
      this.onToggleDisableProduct(key, !checked);
    }
    // Update local object reference (if present) to reflect state immediately
    try { if (p) { if ('enabled' in p) p.enabled = checked; else if ('isEnabled' in p) p.isEnabled = checked; } } catch { }
  }
  async onDeleteMenuProduct(key: string) {
    if (!key) return;
    const ok = window.confirm('¿Estás seguro de que deseas eliminar este producto del catálogo?');
    if (!ok) return;
    // Optimistic local removal
    this.deletedMenuProducts.add(key);
    try { localStorage.setItem(this.DELETED_STORAGE_KEY, JSON.stringify(Array.from(this.deletedMenuProducts))); } catch { }
    // Try backend deletion if available
    try {
      const svc: any = this.menuProductService as any;
      if (svc?.deleteMenuProduct) {
        await svc.deleteMenuProduct(key).toPromise?.() ?? svc.deleteMenuProduct(key).toPromise?.();
      }
    } catch (e) {
      console.warn('No se pudo eliminar en backend, se mantiene oculto localmente', e);
    }
  }

  findTitle = "Buscar producto"
  selectStatus: { name: string }[] = [
    { name: "Churros" },
    { name: "Coxinhas" },
    { name: "Sandwichs" },
    { name: "Bebidas" },
    { name: "Jugos Naturales" }
  ]

  products: { name: string }[] = []
  // Catalog groups for embedded catalog (fallback independent of categories payload)
  catalogGroups: MenuProductGroup[] = [];
  // Top-level Catalog collapsed state (default: closed)
  isCatalogOpen = false;
  // Local visibility management for embedded catalog
  private readonly DISABLED_STORAGE_KEY = 'orders.catalog.disabled';
  private readonly DELETED_STORAGE_KEY = 'orders.catalog.deleted';
  private disabledMenuProducts = new Set<string>();
  private deletedMenuProducts = new Set<string>();

  // --- Pago parcial por grupo ---
  groupOptions: { id: string; name: string; count: number; amount: number }[] = [];
  // Opciones para el combobox atómico (Atomic Design)
  partialGroupOptionsCombo: ComboOption[] = [];
  selectedGroupId: string = '';
  private mpGroupIndex = new Map<string, { id: string; name: string }>();

  // Pago parcial: seguimiento de grupos ya pagados para evitar repetición
  private paidGroups = new Map<string, { amount: number; tip?: number; total?: number; method?: 'card' | 'cash' | 'mix' | string; paidAt: number; items: string[] }>();
  private paidGroupsStorageKey(): string {
    return `orders.groups.paid.${this.orderHashId || 'noid'}`;
  }
  private loadPaidGroups(): void {
    try {
      const raw = localStorage.getItem(this.paidGroupsStorageKey());
      if (!raw) { this.paidGroups = new Map(); return; }
      const obj = JSON.parse(raw);
      const entries = Object.entries(obj || {}).map(([id, v]: any) => [id, v]);
      this.paidGroups = new Map(entries as any);
    } catch { this.paidGroups = new Map(); }
  }
  private savePaidGroups(): void {
    try {
      const obj: Record<string, any> = {};
      this.paidGroups.forEach((v, k) => { obj[k] = v; });
      localStorage.setItem(this.paidGroupsStorageKey(), JSON.stringify(obj));
    } catch { }
  }
  isGroupPaid(id: string): boolean {
    if (!id) return false;
    return this.paidGroups.has(id);
  }
  // Un grupo se considera pagado si aparece en paidGroups o si tiene paymentUuid persistido
  isGroupEffectivelyPaid(id: string): boolean {
    try {
      if (!id) return false;
      if (this.isGroupPaid(id)) return true;
      const grp = this.savedGroups.find(g => g.gid === id);
      const hasPaymentUuid = typeof grp?.paymentUuid === 'string' && grp.paymentUuid.trim().length > 0;
      return !!hasPaymentUuid;
    } catch { return false; }
  }
  private markGroupPaid(id: string, amount: number, items: string[], tip?: number, total?: number, method?: 'card' | 'cash' | 'mix' | string): void {
    try {
      const a = Number(amount) || 0;
      const t = Number(tip) || 0;
      const ttl = Number(total);
      this.paidGroups.set(id, {
        amount: a,
        tip: t,
        total: isNaN(ttl) ? (a + t) : ttl,
        method: method,
        paidAt: Date.now(),
        items: Array.isArray(items) ? items : []
      });
      this.savePaidGroups();
    } catch { }
  }

  // === Agrupar: estado, selección y persistencia ===
  groupingMode = false;
  // submodo: edición de grupo existente (activado al clickear un badge ✓ fuera de modo agrupar)
  groupEditMode = false;
  private selectedGroupUuids = new Set<string>();
  // Selección temporal para creación de nuevo grupo (no persistida)
  private currentNewGroupUuids = new Set<string>();
  // Grupos guardados en localStorage (múltiples grupos, cada uno con su set de ítems)
  private savedGroups: { gid: string; color: string; itemUuids: string[]; createdAt: number; name?: string; paymentUuid?: string }[] = [];
  groupedTotal = 0;
  groupBadgeColor = '#748FFC';
  // Color para modo "nuevo grupo" (crear grupo)
  newGroupBadgeColor = '#FF6B6B';
  // Paleta para colores de grupos guardados (badges distintos)
  private groupColorPalette: string[] = [
    '#FF6B6B', // red
    '#FFA94D', // orange
    '#FFD43B', // yellow
    '#82C91E', // green
    '#4DABF7', // sky blue
    '#748FFC', // indigo
    '#DA77F2', // violet
    '#F783AC', // pink
    '#63E6BE', // teal
    '#F59F00'  // amber
  ];

  private groupSelStorageKey(): string {
    return `orders.group.sel.${this.orderHashId || 'noid'}`;
  }

  private groupsStorageKey(): string {
    return `orders.groups.${this.orderHashId || 'noid'}`;
  }

  private groupIndexStorageKey(): string {
    return `orders.groups.index.${this.orderHashId || 'noid'}`;
  }

  private groupObjectStorageKey(gid: string): string {
    return `orders.group.${this.orderHashId || 'noid'}.${gid}`;
  }

  private loadGroupSelection(): void {
    // No llamar API remota. Usar grupos del payload de la orden si existen;
    // si no, NO usar localStorage ni ninguna persistencia local.
    try {
      const payload = Array.isArray((this.order as any)?.groups) ? (this.order as any).groups : [];
      if (payload.length) {
        this.savedGroups = payload.map((g: any, i: number) => ({
          gid: String(g?.uuid || g?.gid || g?.id || `grp-${Date.now()}-${i}`),
          color: (typeof g?.color === 'string' && g.color.length) ? g.color : this.pickNextGroupColor(),
          itemUuids: (Array.isArray(g?.itemUuids) ? g.itemUuids : []).map((u: any) => String(u)),
          createdAt: Number(g?.createdAt || Date.now()),
          name: (typeof g?.name === 'string' ? g.name : ''),
          paymentUuid: (g?.paymentUuid ? String(g.paymentUuid) : ''),
        }));
        this.afterGroupsLoaded();
        return;
      }
      // Sin payload de grupos: mantener vacío y continuar
      this.savedGroups = [];
      this.afterGroupsLoaded();
    } catch {
      this.savedGroups = [];
      this.afterGroupsLoaded();
    }
  }

  private loadGroupsFromLocalAndMaybeUpload(): void {
    try {
      const rawIndex = localStorage.getItem(this.groupIndexStorageKey());
      if (rawIndex) {
        const gids: string[] = JSON.parse(rawIndex) || [];
        const loaded = gids.map(gid => {
          try {
            const rawObj = localStorage.getItem(this.groupObjectStorageKey(gid));
            const obj = rawObj ? JSON.parse(rawObj) : null;
            return obj || null;
          } catch { return null; }
        }).filter(Boolean) as any[];
        this.savedGroups = loaded.map((g: any, i: number) => ({
          gid: String(g?.gid || g?.id || gids[i] || `grp-${Date.now()}-${i}`),
          color: (typeof g?.color === 'string' && g.color) ? g.color : this.pickNextGroupColor(),
          itemUuids: (Array.isArray(g?.itemUuids) ? g.itemUuids : (Array.isArray(g?.items) ? g.items : (Array.isArray(g?.uuids) ? g.uuids : []))).map((u: any) => String(u)),
          createdAt: Number(g?.createdAt || g?.created_at || Date.now()),
          name: (typeof g?.name === 'string' ? g.name : ''),
          paymentUuid: (g?.paymentUuid ? String(g.paymentUuid) : ''),
        }));
      } else {
        // Reusar la lógica original de migraciones
        const rawArray = localStorage.getItem(this.groupsStorageKey());
        if (rawArray) {
          const arr = JSON.parse(rawArray) || [];
          if (Array.isArray(arr)) {
            if (arr.length > 0 && typeof arr[0] === 'string') {
              const gid = `grp-${Date.now()}`;
              this.savedGroups = [{ gid, color: this.pickNextGroupColor(), itemUuids: (arr as any[]).map(u => String(u)), createdAt: Date.now(), name: '', paymentUuid: '' }];
            } else if (arr.length > 0 && Array.isArray(arr[0])) {
              this.savedGroups = (arr as any[]).map((uuids: any, i: number) => ({
                gid: `grp-${Date.now()}-${i}`,
                color: this.pickNextGroupColor(),
                itemUuids: (Array.isArray(uuids) ? uuids : []).map((u: any) => String(u)),
                createdAt: Date.now(),
                name: '',
                paymentUuid: '',
              }));
            } else if (arr.length > 0 && typeof arr[0] === 'object') {
              this.savedGroups = (arr as any[]).map((g: any, i: number) => ({
                gid: String(g?.gid || g?.id || `grp-${Date.now()}-${i}`),
                color: (typeof g?.color === 'string' && g.color) ? g.color : this.pickNextGroupColor(),
                itemUuids: (Array.isArray(g?.itemUuids) ? g.itemUuids : (Array.isArray(g?.items) ? g.items : (Array.isArray(g?.uuids) ? g.uuids : []))).map((u: any) => String(u)),
                createdAt: Number(g?.createdAt || g?.created_at || Date.now()),
                name: (typeof g?.name === 'string' ? g.name : ''),
                paymentUuid: (g?.paymentUuid ? String(g.paymentUuid) : ''),
              }));
            } else {
              this.savedGroups = [];
            }
          } else {
            this.savedGroups = [];
          }
          this.saveSavedGroups();
          localStorage.removeItem(this.groupsStorageKey());
        } else {
          const legacyRaw = localStorage.getItem(this.groupSelStorageKey());
          const legacyArr = legacyRaw ? JSON.parse(legacyRaw) : [];
          if (Array.isArray(legacyArr) && legacyArr.length > 0) {
            const gid = `grp-${Date.now()}`;
            this.savedGroups = [{ gid, color: this.groupBadgeColor, itemUuids: legacyArr.map((u: any) => String(u)), createdAt: Date.now(), name: '', paymentUuid: '' }];
            localStorage.removeItem(this.groupSelStorageKey());
            this.saveSavedGroups();
          } else {
            this.savedGroups = [];
          }
        }
      }
    } catch { this.savedGroups = []; }
    this.afterGroupsLoaded();
  }

  private afterGroupsLoaded(): void {
    // Asegurar colores únicos para cada grupo guardado
    this.ensureDistinctGroupColors();
    // Selección activa: si hay grupos, tomar el primero como seleccionado por defecto
    const first = this.savedGroups[0];
    this.selectedGroupUuids = new Set(first ? first.itemUuids : []);
    this.recomputeGroupedTotal();
    // Recalcular opciones unificadas (catálogo + grupos guardados)
    this.computeGroupOptions();
  }

  private saveGroupSelection(): void {
    // Guardar la edición del grupo activo sobre la estructura de múltiples grupos
    try {
      if (this.groupEditMode && this.selectedGroupId) {
        const idx = this.savedGroups.findIndex(g => g.gid === this.selectedGroupId);
        if (idx >= 0) {
          this.savedGroups[idx].itemUuids = Array.from(this.selectedGroupUuids.values());
          // No persistir en localStorage; sólo actualizar opciones en memoria
          this.computeGroupOptions();
          return;
        }
      }
      // Si no hay grupo activo, no hacemos nada aquí
    } catch { }
  }

  private saveSavedGroups(): void {
    // No usar localStorage ni backend para grupos; sólo recalcular opciones en memoria
    try {
      this.computeGroupOptions();
    } catch { }
  }

  // Escoger un color disponible de la paleta para el próximo grupo
  private pickNextGroupColor(): string {
    const used = new Set(this.savedGroups.map(g => g.color));
    for (const c of this.groupColorPalette) {
      if (!used.has(c)) return c;
    }
    // Si todos están usados, rotar por longitud
    return this.groupColorPalette[this.savedGroups.length % this.groupColorPalette.length];
  }

  // Normaliza colores repetidos o faltantes en grupos ya guardados
  private ensureDistinctGroupColors(): void {
    const used = new Set<string>();
    let changed = false;
    this.savedGroups.forEach(g => {
      if (!g.color || used.has(g.color)) {
        const next = this.pickColorFromUsed(used);
        g.color = next;
        changed = true;
      }
      used.add(g.color);
    });
    if (changed) this.saveSavedGroups();
  }

  private pickColorFromUsed(used: Set<string>): string {
    for (const c of this.groupColorPalette) {
      if (!used.has(c)) return c;
    }
    return this.groupColorPalette[used.size % this.groupColorPalette.length];
  }

  toggleGroupingMode(): void {
    this.groupingMode = !this.groupingMode;
    // Al entrar por botón, se crean nuevas agrupaciones: solo interactúan NO agrupados
    if (this.groupingMode) this.groupEditMode = false;
    else this.groupEditMode = false;
    // Al entrar en modo agrupar, no fijamos grupo hasta seleccionar
    if (this.groupingMode) {
      // Elegir color único para el nuevo grupo temporal
      this.newGroupBadgeColor = this.pickNextGroupColor();
      // En creación de nuevo grupo no fijamos selectedGroupId
      this.selectedGroupId = '';
      // Reiniciar selección temporal de nuevo grupo
      this.currentNewGroupUuids.clear();
    } else {
      // Al salir del modo agrupar: si hay selección nueva, persistirla como parte del grupo guardado
      if (this.currentNewGroupUuids.size > 0) {
        // Crear un grupo nuevo, con color de nuevo grupo
        const gid = `grp-${Date.now()}`;
        const newGroup = {
          gid,
          color: this.pickNextGroupColor(),
          itemUuids: Array.from(this.currentNewGroupUuids.values()),
          createdAt: Date.now(),
          name: this.generateDefaultGroupName()
        };
        this.savedGroups.push(newGroup);
        this.currentNewGroupUuids.clear();
        this.saveSavedGroups();
        // Seleccionar como activo el grupo recién creado
        this.selectedGroupId = gid;
        this.selectedGroupUuids = new Set(this.savedGroups.find(g => g.gid === gid)?.itemUuids || []);
      }
      // Recalcular total agrupado sobre la selección persistida
      this.recomputeGroupedTotal();
    }
  }

  // true si hay selección actual guardada
  get hasGroupSelection(): boolean {
    return (this.savedGroups?.length || 0) > 0;
  }

  // true si el nuevo grupo temporal ya tiene ítems asociados
  get hasNewGroupSelection(): boolean {
    try {
      return this.currentNewGroupUuids.size > 0;
    } catch { return false; }
  }

  private generateDefaultGroupName(): string {
    const nextIndex = (Array.isArray(this.savedGroups) ? this.savedGroups.length : 0) + 1;
    return `Grupo #${nextIndex}`;
  }

  isItemSelected(uuid: string): boolean {
    return this.selectedGroupUuids.has(uuid);
  }

  // true si el ítem está seleccionado en el nuevo grupo (temporal)
  isItemNewSelected(uuid: string): boolean {
    return this.currentNewGroupUuids.has(uuid);
  }

  // Pertenece a algún grupo guardado (cualquiera)
  isItemInAnySavedGroup(uuid: string): boolean {
    return this.savedGroups.some(g => g.itemUuids.includes(uuid));
  }
  // Pertenece a un grupo marcado como pagado
  isItemInPaidGroup(uuid: string): boolean {
    try {
      return this.savedGroups.some(g => g.itemUuids.includes(uuid) && this.isGroupEffectivelyPaid(g.gid));
    } catch { return false; }
  }

  // Color de badge para un ítem según su contexto
  getGroupBadgeColor(uuid: string): string {
    // Para coincidir con el color de fondo del item,
    // reutilizamos la misma lógica y opacidad de getItemBackgroundColor.
    const bg = this.getItemBackgroundColor(uuid);
    if (bg) return bg;

    // Sin color de fondo calculado, aplicamos el fallback por contexto.
    if (this.groupingMode) {
      // En modo agrupar, si no hay color de grupo (no pertenece o no seleccionado), badge neutro.
      return '#9E9E9E';
    }
    // Fuera de modo, usar el color por defecto del badge
    return this.groupBadgeColor;
  }

  // Ocultar el botón de “Agrupar” cuando todos los ítems
  // ya pertenecen a algún grupo guardado
  canEnterGroupingMode(): boolean {
    try {
      return this.orderList.some(it => !this.isItemInAnySavedGroup(it.uuid));
    } catch { return false; }
  }

  private hexToRgba(hex: string, alpha: number): string {
    try {
      let h = hex.trim();
      if (!h) return '';
      if (h.startsWith('#')) h = h.slice(1);
      if (h.length === 3) {
        const r = parseInt(h[0] + h[0], 16);
        const g = parseInt(h[1] + h[1], 16);
        const b = parseInt(h[2] + h[2], 16);
        return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
      }
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
    } catch {
      return '';
    }
  }

  // Color de fondo del ítem seleccionado, igual al del badge
  getItemBackgroundColor(uuid: string): string {
    const alpha = 0.16; // opacidad sutil para fondo
    if (this.groupingMode) {
      // Nuevo grupo temporal: si el ítem ya tiene un color de grupo, respetarlo
      if (!this.groupEditMode && this.isItemNewSelected(uuid)) {
        const persisted = this.savedGroups.find(g => g.itemUuids.includes(uuid));
        const base = persisted?.color || this.newGroupBadgeColor;
        return this.hexToRgba(base, alpha);
      }
      // Edición de grupo existente
      if (this.groupEditMode && this.isItemSelected(uuid)) {
        const grp = this.savedGroups.find(g => g.gid === this.selectedGroupId);
        const color = grp?.color || this.groupBadgeColor;
        return this.hexToRgba(color, alpha);
      }
      // No seleccionado en modo agrupar: si ya pertenece a un grupo, mostrar su fondo
      const grp = this.savedGroups.find(g => g.itemUuids.includes(uuid));
      return grp?.color ? this.hexToRgba(grp.color, alpha) : '';
    }
    // Fuera de modo agrupar: si pertenece a algún grupo, usar su color con opacidad
    const grp = this.savedGroups.find(g => g.itemUuids.includes(uuid));
    return grp?.color ? this.hexToRgba(grp.color, alpha) : '';
  }

  onToggleGroupItem(uuid: string): void {
    if (!this.groupingMode) return;
    if (!this.canGroupItem(uuid)) return;
    // Si aún no hay grupo fijado, lo definimos con el primer ítem interactuado
    if (this.groupEditMode && !this.selectedGroupId) {
      // En edición de grupo, si no hay grupo seleccionado aún,
      // infiere el grupo guardado (gid) al que pertenece el ítem
      const grpSaved = this.savedGroups.find(g => g.itemUuids.includes(uuid));
      this.selectedGroupId = grpSaved?.gid || '';
      this.selectedGroupUuids = new Set(grpSaved?.itemUuids || []);
    }
    if (this.groupEditMode) {
      // Editando grupo existente: operar sobre la selección persistida
      if (this.selectedGroupUuids.has(uuid)) this.selectedGroupUuids.delete(uuid);
      else this.selectedGroupUuids.add(uuid);
      this.saveGroupSelection();
    } else {
      // Creando nuevo grupo: si hay grupos existentes y estamos agregando, ofrecer selección
      const isAdding = !this.currentNewGroupUuids.has(uuid);
      const hasExistingGroups = (Array.isArray(this.savedGroups) ? this.savedGroups.length : 0) > 0;
      if (isAdding && hasExistingGroups) {
        // Excluir grupos con pago asociado
        const filtered = this.savedGroups.filter(g => !this.isGroupEffectivelyPaid(g.gid));
        const groups = filtered.map((g, i) => ({
          gid: g.gid,
          name: (g?.name && g.name.trim().length > 0) ? g.name : `Grupo #${i + 1}`,
          color: g.color,
        }));
        const ref = this.dialog.open(GroupAssignDialogComponent, {
          data: { groups, uuid, newColor: this.newGroupBadgeColor },
          width: '420px',
        });
        ref.afterClosed().subscribe((result: GroupAssignDialogResult | undefined) => {
          if (!result) return;
          if (result.kind === 'existing' && result.gid) {
            const grp = this.savedGroups.find((sg) => sg.gid === result.gid);
            if (grp && !grp.itemUuids.includes(uuid)) grp.itemUuids.push(uuid);
            this.saveSavedGroups();
            this.recomputeGroupedTotal();
          } else if (result.kind === 'new') {
            this.currentNewGroupUuids.add(uuid);
            this.recomputeGroupedTotal();
          }
        });
        return; // Evitar el toggle inmediato; lo maneja el modal
      }

      // Operar sobre selección temporal cuando no hay grupos o estamos quitando
      if (this.currentNewGroupUuids.has(uuid)) this.currentNewGroupUuids.delete(uuid);
      else this.currentNewGroupUuids.add(uuid);
      // No persistimos hasta que el flujo lo requiera
      this.recomputeGroupedTotal();
    }
  }

  // Al hacer click sobre el badge en modo vista: entrar a edición del grupo
  onClickGroupBadge(uuid: string): void {
    if (!this.groupingMode) {
      // Entrar al modo de edición del grupo existente: sólo interactúan los ítems del grupo elegido
      this.groupingMode = true;
      this.groupEditMode = true;
      // Fijar el grupo activo según el ítem del badge clicado dentro de savedGroups
      const grp = this.savedGroups.find(g => g.itemUuids.includes(uuid));
      this.selectedGroupId = grp?.gid || '';
      this.selectedGroupUuids = new Set(grp?.itemUuids || []);
      this.recomputeGroupedTotal();
      return;
    }
    this.onToggleGroupItem(uuid);
  }

  // Interacción en modo agrupar:
  // - Crear nuevo grupo (groupEditMode=false): sólo ítems NO agrupados son interactivos
  // - Editar grupo (groupEditMode=true): sólo ítems del grupo seleccionado son interactivos
  // Iniciar edición de un grupo desde el toolbar
  startEditGroup(gid: string): void {
    try {
      this.groupingMode = true;
      this.groupEditMode = true;
      const grp = this.savedGroups.find(g => g.gid === gid);
      this.selectedGroupId = grp?.gid || '';
      this.selectedGroupUuids = new Set(grp?.itemUuids || []);
      this.recomputeGroupedTotal();
    } catch { }
  }

  // Editar grupo: cambiar nombre, desasociar ítems y borrar (si no tiene pago)
  async onEditGroupName(gid: string): Promise<void> {
    try {
      const grp = this.savedGroups.find(g => g.gid === gid);
      if (!grp) return;
      const effectiveItems = this.getNonCanceledItems(this.orderList);
      const set = new Set((grp.itemUuids || []).map(u => String(u)));
      const items: GroupEditItemView[] = effectiveItems
        .filter(it => set.has(it.uuid))
        .map(it => ({ uuid: it.uuid, name: it.name || '', qty: Number(it.qty || 0), price: Number(it.price || 0) }));
      const canDelete = !this.isGroupEffectivelyPaid(gid);

      const data: GroupEditDialogData = {
        gid,
        name: (grp.name || ''),
        color: grp.color,
        items,
        canDelete
      };
      const ref = this.dialog.open(GroupEditDialogComponent, { data, width: '420px' });
      const result = (await ref.afterClosed().toPromise()) as GroupEditDialogResult | undefined;
      if (!result) return; // cancelado

      if (result.action === 'save') {
        // Actualizar nombre
        if (typeof result.name === 'string') grp.name = result.name.trim();
        // Actualizar asociación de ítems (los no seleccionados quedan desasociados)
        const keep = new Set((result.keepItemUuids || []).map(u => String(u)));
        grp.itemUuids = Array.from(keep.values());
        // Si este grupo estaba activo en edición, sincronizar selección
        if (this.groupEditMode && this.selectedGroupId === gid) {
          this.selectedGroupUuids = new Set(grp.itemUuids.map(u => String(u)));
        }
        this.saveSavedGroups();
        this.recomputeGroupedTotal();
      }
      if (result.action === 'delete') {
        if (!canDelete) return; // seguridad
        // Confirmar eliminación usando componente atómico compartido
        const confirmRef = this.dialog.open(ConfirmDialogComponent, {
          width: '320px',
          data: { itemName: `Eliminar el grupo "${grp.name || 'Sin nombre'}" y desasociar sus ítems?` }
        });
        const confirmed = await confirmRef.afterClosed().toPromise();
        if (!confirmed) return;
        // Remover el grupo y desasociar sus ítems
        this.savedGroups = this.savedGroups.filter(g => g.gid !== gid);
        if (this.selectedGroupId === gid) {
          this.groupEditMode = false;
          this.selectedGroupId = '';
          this.selectedGroupUuids.clear();
        }
        this.saveSavedGroups();
        this.recomputeGroupedTotal();
      }
    } catch { }
  }

  // Eliminar grupo desde el badge (sólo si no tiene pago asociado)
  async onDeleteSavedGroup(gid: string, ev?: MouseEvent): Promise<void> {
    try {
      ev?.stopPropagation();
      const grp = this.savedGroups.find(g => g.gid === gid);
      if (!grp) return;
      const canDelete = !this.isGroupEffectivelyPaid(gid);
      if (!canDelete) return;
      // Warning visual al usuario antes de confirmar la eliminación
      this.toast.warning('Estás a punto de borrar este grupo', 'toast-top-right');
      const confirmRef = this.dialog.open(ConfirmDialogComponent, {
        width: '320px',
        data: { itemName: `Eliminar el grupo "${grp.name || 'Sin nombre'}" y desasociar sus ítems?` }
      });
      const confirmed = await confirmRef.afterClosed().toPromise();
      if (!confirmed) return;
      this.savedGroups = this.savedGroups.filter(g => g.gid !== gid);
      if (this.selectedGroupId === gid) {
        this.groupEditMode = false;
        this.selectedGroupId = '';
        this.selectedGroupUuids.clear();
      }
      this.saveSavedGroups();
      this.recomputeGroupedTotal();
    } catch { }
  }
  canGroupItem(uuid: string): boolean {
    try {
      if (this.groupEditMode) {
        if (!this.selectedGroupId) return false;
        // En edición: sólo los ítems que pertenecen al grupo seleccionado
        return this.selectedGroupUuids.has(uuid);
      }
      // Creación de nuevo grupo: deshabilitar los ítems que pertenecen a cualquier grupo guardado
      return !this.isItemInAnySavedGroup(uuid);
    } catch { return false; }
  }

  private recomputeGroupedTotal(): void {
    try {
      const activeSet = (this.groupingMode && !this.groupEditMode) ? this.currentNewGroupUuids : this.selectedGroupUuids;
      const items = this.getNonCanceledItems(this.orderList).filter(it => activeSet.has(it.uuid));
      const tot = items.reduce((acc, it) => acc + (Number(it.qty || 0) * Number(it.price || 0)), 0);
      this.groupedTotal = tot;
    } catch {
      this.groupedTotal = 0;
    }
  }

  get groupedTotalAfterDiscount(): number {
    const disc = Math.max(0, Math.floor(Number(this.discountAmount || 0)));
    // Permitir negativos si el descuento supera el total del grupo
    return (Number(this.groupedTotal || 0) - disc);
  }

  get groupedSuggestedTip(): number {
    // No sugerir propina cuando el total del grupo tras descuento es negativo
    return Math.round(Math.max(0, this.groupedTotalAfterDiscount) * 0.10);
  }

  // Pagos ejecutados: mostrar monto pagado en el voucher
  private get backendPaidTotal(): number {
    try {
      const pay: any = (this.order as any)?.payment || {};
      const payments: any[] = Array.isArray((this.order as any)?.payments) ? (this.order as any).payments : [];
      const unified = Number(pay?.paidTotal ?? pay?.paid ?? 0) || 0;
      const fromLines = payments.reduce((acc: number, p: any) => {
        const unifiedLine = p?.amount !== undefined ? Number(p.amount) : NaN;
        const card = Number(p?.cardAmount ?? 0) || 0;
        const cash = Number(p?.cashAmount ?? 0) || 0;
        const line = !isNaN(unifiedLine) && unifiedLine > 0 ? unifiedLine : (card + cash);
        return acc + (Number(line) || 0);
      }, 0);
      return Math.max(unified, fromLines);
    } catch { return 0; }
  }
  // Total pagado BASE (sin propina) desde backend
  private get backendPaidBaseTotal(): number {
    try {
      const pay: any = (this.order as any)?.payment || {};
      const payments: any[] = Array.isArray((this.order as any)?.payments) ? (this.order as any).payments : [];
      const unified = Number(pay?.paidTotal ?? pay?.paid ?? 0) || 0;
      const tips = payments.reduce((acc: number, p: any) => acc + (Number(p?.tipAmount ?? 0) || 0), 0);
      const unifiedBase = Math.max(0, unified - tips);
      const fromLinesBase = payments.reduce((acc: number, p: any) => {
        const unifiedLine = p?.amount !== undefined ? Number(p.amount) : NaN;
        const tipLine = Number(p?.tipAmount ?? 0) || 0;
        const card = Number(p?.cardAmount ?? 0) || 0;
        const cash = Number(p?.cashAmount ?? 0) || 0;
        const baseLine = !isNaN(unifiedLine) && unifiedLine > 0 ? Math.max(0, unifiedLine - tipLine) : (card + cash);
        return acc + (Number(baseLine) || 0);
      }, 0);
      return Math.max(unifiedBase, fromLinesBase);
    } catch { return 0; }
  }
  private get partialPaidTotal(): number {
    try {
      let sum = 0;
      this.paidGroups.forEach(v => {
        const total = Number(v?.total);
        const fallback = (Number(v?.amount) || 0) + (Number(v?.tip) || 0);
        sum += (!isNaN(total) && total > 0) ? total : fallback;
      });
      return sum;
    } catch { return 0; }
  }
  // Total pagado BASE (sin propina) desde pagos locales de grupos
  private get partialPaidBaseTotal(): number {
    try {
      let sum = 0;
      this.paidGroups.forEach(v => {
        sum += Math.max(0, Number(v?.amount) || 0);
      });
      return sum;
    } catch { return 0; }
  }
  get paidTotalDisplay(): number {
    const backend = this.backendPaidTotal;
    return backend > 0 ? backend : this.partialPaidTotal;
  }
  // Restante traído desde backend (si disponible)
  private get backendRemaining(): number {
    try {
      const rem = Number((this.order as any)?.payment?.remaining ?? 0) || 0;
      return rem;
    } catch { return 0; }
  }
  // Restante por ingresar (propina opcional): usa total sin propina
  get remainingDisplay(): number {
    try {
      // Mostrar el valor del backend tal cual, incluidos negativos
      const backendRem = Number(this.backendRemaining || 0);
      if (!isNaN(backendRem)) return Math.round(backendRem);
      // Si el backend no provee (o es 0), calcular con base y pagos
      const base = Number(this.totalAfterDiscount || 0);
      const paidBase = Number((this.backendPaidBaseTotal > 0 ? this.backendPaidBaseTotal : this.partialPaidBaseTotal) || 0);
      return Math.round(base - paidBase);
    } catch { return 0; }
  }
  // Detalle de pagos por grupo para la vista
  get paidGroupsDetails(): { id: string; name: string; amount: number; tip: number; total: number; method?: string }[] {
    try {
      const res: { id: string; name: string; amount: number; tip: number; total: number; method?: string }[] = [];
      const ordinalByGid = new Map<string, number>();
      this.savedGroups.forEach((g, i) => ordinalByGid.set(g.gid, i + 1));

      // 1) Primero, usar los grupos pagados en memoria/localStorage
      this.paidGroups.forEach((v, gid) => {
        const grp = this.savedGroups.find(g => g.gid === gid);
        const displayName = (grp?.name && grp.name.trim().length > 0)
          ? grp.name
          : `Grupo #${ordinalByGid.get(gid) ?? ''}`.trim();
        const amount = Math.max(0, Number(v?.amount) || 0);
        const tip = Number(v?.tip) || 0;
        const total = (!isNaN(Number(v?.total)) && Number(v?.total) > 0)
          ? Number(v?.total)
          : amount + tip;
        res.push({ id: gid, name: displayName, amount, tip, total, method: (v?.method || '') });
      });

      // 2) Luego, incluir fallback para grupos con paymentUuid (pagados en backend)
      //    que no estén presentes en paidGroups.
      (this.savedGroups || []).forEach((g, idx) => {
        const alreadyIncluded = res.some(r => r.id === g.gid);
        const hasPaymentUuid = typeof g?.paymentUuid === 'string' && g.paymentUuid.trim().length > 0;
        if (!alreadyIncluded && hasPaymentUuid) {
          const displayName = (g?.name && g.name.trim().length > 0)
            ? g.name
            : `Grupo #${ordinalByGid.get(g.gid) ?? (idx + 1)}`.trim();
          // Intentar obtener la línea de pago del backend asociada al grupo
          const payments: any[] = Array.isArray((this.order as any)?.payments) ? (this.order as any).payments : [];
          const pLine = payments.find((p: any) => String(p?.uuid || '') === String(g.paymentUuid || ''));

          // Datos provenientes del backend
          const unifiedLine = pLine?.amount !== undefined ? Number(pLine.amount) : NaN;
          const cardLine = Number(pLine?.cardAmount ?? 0) || 0;
          const cashLine = Number(pLine?.cashAmount ?? 0) || 0;
          const tipLine = Number(pLine?.tipAmount ?? 0) || 0;
          const totalFromLine = (!isNaN(unifiedLine) && unifiedLine > 0) ? unifiedLine : (cardLine + cashLine);

          // Calcular el monto base del grupo desde sus ítems como respaldo
          const set = new Set((g.itemUuids || []).map(u => String(u)));
          const amountFromItems = this.getNonCanceledItems(this.orderList)
            .filter(it => set.has(it.uuid))
            .reduce((acc, it) => acc + (Number(it.qty) * Number(it.price)), 0);

          // Si tenemos total y propina del backend, separar base y propina
          let amount = amountFromItems;
          let tip = 0;
          let total = amountFromItems;
          let method: string | undefined = undefined;

          if (totalFromLine > 0) {
            if (tipLine > 0 && totalFromLine >= tipLine) {
              // Asumir que el total de la línea incluye la propina
              amount = Math.max(0, totalFromLine - tipLine);
              tip = tipLine;
              total = totalFromLine;
            } else {
              // Sin propina explícita, usar total de la línea como base
              amount = totalFromLine;
              tip = 0;
              total = totalFromLine;
            }
          } else {
            // Sin total explícito del backend, usar items y la propina si está disponible
            amount = amountFromItems;
            tip = tipLine;
            total = amountFromItems + tipLine;
          }

          // Método de pago desde el backend
          if (cardLine > 0 && cashLine > 0) method = 'mix';
          else if (cardLine > 0) method = 'card';
          else if (cashLine > 0) method = 'cash';
          else if ((pLine?.method || '').toString().length > 0) method = String(pLine.method);

          res.push({ id: g.gid, name: displayName, amount, tip, total, method });
        }
      });

      return res.sort((a, b) => a.name.localeCompare(b.name));
    } catch { return []; }
  }
  // Exponer grupos guardados para la vista (sin romper encapsulamiento)
  get groupsSaved(): { gid: string; color: string; itemUuids: string[]; createdAt: number; name?: string; paymentUuid?: string }[] {
    return this.savedGroups;
  }
  // Nombre a mostrar para un grupo guardado (evita acceso directo en template)
  getGroupDisplayName(g: any, index: number): string {
    try {
      const raw = ((g?.name || '') as string).toString().trim();
      return raw.length > 0 ? raw : `Grupo #${index + 1}`;
    } catch { return `Grupo #${index + 1}`; }
  }
  getMethodLabel(m?: string): string {
    const mm = (m || '').toLowerCase();
    if (mm === 'card') return 'TD / TC';
    if (mm === 'cash') return 'Efectivo';
    if (mm === 'mix') return 'Mixto';
    return '-';
  }
  // Calcular el total de un grupo guardado a partir de sus ítems
  getGroupTotal(gid: string): number {
    try {
      const grp = this.savedGroups.find(g => g.gid === gid);
      if (!grp) return 0;
      const set = new Set((grp.itemUuids || []).map((u: any) => String(u)));
      // Coincidir por uuid de ítem o por menuProductUuid (esquemas legacy)
      const items = this.getNonCanceledItems(this.orderList).filter(it => set.has(it.uuid) || set.has(String((it as any)?.menuProductUuid || '')));
      return items.reduce((acc, it) => acc + (Number(it.qty || 0) * Number(it.price || 0)), 0);
    } catch { return 0; }
  }

  private buildMenuProductGroupIndex(): void {
    try {
      this.mpGroupIndex.clear();
      const groups = Array.isArray(this.catalogGroups) ? this.catalogGroups : [];
      groups.forEach((g: any) => {
        const idRaw = (g?.categoryId ?? g?.category_id ?? g?.id ?? g?.categoryUuid ?? g?.uuid ?? g?.categoryName);
        const id = (idRaw !== undefined && idRaw !== null) ? String(idRaw) : (g?.categoryName || 'grupo');
        const name = (g?.categoryName || g?.name || `Grupo ${id}`);
        const items = Array.isArray(g?.items) ? g.items : [];
        items.forEach((mp: any) => {
          const key = (mp?.uuid || mp?.id);
          if (key) this.mpGroupIndex.set(String(key), { id, name });
        });
      });
    } catch { }
  }

  // Sincroniza nombres y estructura de grupos desde la orden (si backend los envía)
  private syncSavedGroupsFromOrder(): void {
    try {
      const rawGroups: any[] = Array.isArray((this.order as any)?.groups) ? (this.order as any).groups : [];
      if (!rawGroups.length) return;
      const byGid = new Map<string, any>();
      rawGroups.forEach((g: any) => {
        const gid = String(g?.uuid || g?.gid || g?.id || '');
        if (!gid) return;
        byGid.set(gid, g);
      });

      let changed = false;
      // Actualizar nombres y uuids de ítems de grupos existentes
      this.savedGroups.forEach((sg) => {
        const api = byGid.get(String(sg.gid));
        if (api) {
          const name = (typeof api?.name === 'string') ? api.name : sg.name;
          const itemUuids = Array.isArray(api?.itemUuids) ? api.itemUuids.map((u: any) => String(u)) : sg.itemUuids;
          const color = (typeof api?.color === 'string' && api.color.length) ? api.color : sg.color;
          const paymentUuid = (api?.paymentUuid !== undefined && api?.paymentUuid !== null)
            ? String(api.paymentUuid || '')
            : (sg.paymentUuid || '');
          if (name && name !== sg.name) { sg.name = name; changed = true; }
          // Si backend incluye los itemUuids y difieren, actualizamos
          if (Array.isArray(itemUuids) && itemUuids.length && JSON.stringify(itemUuids) !== JSON.stringify(sg.itemUuids)) {
            sg.itemUuids = itemUuids; changed = true;
          }
          if (color && color !== sg.color) { sg.color = color; changed = true; }
          if ((paymentUuid || '') !== (sg.paymentUuid || '')) { sg.paymentUuid = paymentUuid || ''; changed = true; }
        }
      });

      // Insertar grupos nuevos del backend no presentes localmente
      const existingGids = new Set(this.savedGroups.map(g => String(g.gid)));
      rawGroups.forEach((api) => {
        const gid = String(api?.uuid || api?.gid || api?.id || '');
        if (!gid || existingGids.has(gid)) return;
        const name = (typeof api?.name === 'string') ? api.name : '';
        const itemUuids = Array.isArray(api?.itemUuids) ? api.itemUuids.map((u: any) => String(u)) : [];
        const color = (typeof api?.color === 'string' && api.color.length) ? api.color : this.pickNextGroupColor();
        const paymentUuid = (api?.paymentUuid !== undefined && api?.paymentUuid !== null) ? String(api.paymentUuid || '') : '';
        this.savedGroups.push({
          gid,
          color,
          itemUuids,
          createdAt: Date.now(),
          name,
          paymentUuid
        });
        changed = true;
      });

      if (changed) {
        this.saveSavedGroups();
        this.computeGroupOptions();
      }
    } catch { }
  }

  private getGroupForItem(it: ItemOrder): { id: string; name: string } | undefined {
    try {
      const key = (it?.menuProductUuid || '').toString();
      if (!key) return undefined;
      return this.mpGroupIndex.get(key);
    } catch { return undefined; }
  }

  private computeGroupOptions(): void {
    try {
      const mapById = new Map<string, { id: string; name: string; count: number; amount: number }>();
      const effectiveItems = this.getNonCanceledItems(this.orderList);

      // 1) Opciones desde grupos de catálogo (por categoría)
      effectiveItems.forEach((it) => {
        const grp = this.getGroupForItem(it);
        if (!grp) return;
        const cur = mapById.get(grp.id) || { id: grp.id, name: grp.name, count: 0, amount: 0 };
        const qty = Number(it.qty) || 0;
        const price = Number(it.price) || 0;
        cur.count += qty;
        cur.amount += (qty * price);
        mapById.set(grp.id, cur);
      });

      // 2) Opciones desde grupos creados (guardados por el usuario)
      const saved = Array.isArray(this.savedGroups) ? this.savedGroups : [];
      saved.forEach((g, i) => {
        const set = new Set((g.itemUuids || []).map((u: any) => String(u)));
        const items = effectiveItems.filter(it => set.has(it.uuid) || set.has(String((it as any)?.menuProductUuid || '')));
        if (items.length === 0) return;
        const count = items.reduce((acc, it) => acc + (Number(it.qty) || 0), 0);
        const amount = items.reduce((acc, it) => acc + ((Number(it.qty) || 0) * (Number(it.price) || 0)), 0);
        const id = g.gid;
        const name = (g?.name && g.name.trim().length > 0) ? g.name : `Grupo #${i + 1}`;
        mapById.set(id, { id, name, count, amount });
      });

      this.groupOptions = Array.from(mapById.values()).sort((a, b) => a.name.localeCompare(b.name));
      // Construir opciones SOLO para grupos guardados en el combobox atómico
      const savedListForCombo = Array.isArray(this.savedGroups) ? this.savedGroups : [];
      const effectiveItems2 = effectiveItems;
      // Mapear ordinal por gid según el orden original de savedGroups (1-based)
      const ordinalByGid = new Map<string, number>();
      savedListForCombo.forEach((g, i) => ordinalByGid.set(g.gid, i + 1));
      this.partialGroupOptionsCombo = savedListForCombo
        // Ocultar opciones de grupos ya pagados o con paymentUuid asociado
        .filter(g => {
          const hasPaymentUuid = typeof g?.paymentUuid === 'string' && g.paymentUuid.trim().length > 0;
          return !this.isGroupPaid(g.gid) && !hasPaymentUuid;
        })
        .map((g, i) => {
          const set = new Set((g.itemUuids || []).map((u: any) => String(u)));
          const items = effectiveItems2.filter(it => set.has(it.uuid) || set.has(String((it as any)?.menuProductUuid || '')));
          const count = items.reduce((acc, it) => acc + (Number(it.qty) || 0), 0);
          const amount = items.reduce((acc, it) => acc + ((Number(it.qty) || 0) * (Number(it.price) || 0)), 0);
          const id = g.gid;
          const ordinal = ordinalByGid.get(g.gid) ?? (i + 1);
          const displayName = (g?.name && g.name.trim().length > 0) ? g.name : `Grupo #${ordinal}`;
          return {
            value: id,
            label: `${displayName} (${count}) - $${this.formatPlainNumber(amount)}`,
          } as ComboOption;
        });
      // Emitir JSON diagnóstico de montos por grupo para validación rápida en consola
      try {
        this.logGroupAmountsDiagnostics(effectiveItems);
      } catch (e) {
        console.warn('No se pudo generar diagnóstico de grupos', e);
      }
      // Reset selección si el grupo desapareció
      if (!this.partialGroupOptionsCombo.some(o => o.value === this.selectedGroupId)) {
        this.selectedGroupId = '';
      }
    } catch { this.groupOptions = []; this.partialGroupOptionsCombo = []; }
  }

  // Genera y vuelca en consola un JSON con el detalle de conteos y montos por grupo
  private logGroupAmountsDiagnostics(effectiveItems: any[]): void {
    const saved = Array.isArray(this.savedGroups) ? this.savedGroups : [];
    const diagnostics = saved.map((g, i) => {
      const set = new Set((g.itemUuids || []).map((u: any) => String(u)));
      const matched = effectiveItems.filter((it: any) => set.has(String(it?.uuid)) || set.has(String(it?.menuProductUuid || '')));
      const matchedSummary = matched.map((it: any) => ({
        uuid: String(it?.uuid),
        menuProductUuid: it?.menuProductUuid ?? null,
        qty: Number(it?.qty) || 0,
        price: Number(it?.price) || 0,
        lineTotal: ((Number(it?.qty) || 0) * (Number(it?.price) || 0)),
      }));
      const matchedUuidSet = new Set(matchedSummary.map((m: any) => String(m.uuid)));
      const unmatchedUuids = Array.from(set).filter(u => !matchedUuidSet.has(String(u)));
      const count = matchedSummary.reduce((acc: number, m: any) => acc + (Number(m.qty) || 0), 0);
      const amount = matchedSummary.reduce((acc: number, m: any) => acc + (Number(m.lineTotal) || 0), 0);
      return {
        index: i + 1,
        gid: String(g.gid),
        name: `Grupo #${i + 1}`,
        itemUuids: (g.itemUuids || []).map((u: any) => String(u)),
        matchedItems: matchedSummary,
        unmatchedUuids,
        count,
        amount,
      };
    });
    const payload = {
      orderHashId: this.orderHashId,
      groups: diagnostics,
      totals: {
        totalCount: diagnostics.reduce((a, d) => a + d.count, 0),
        totalAmount: diagnostics.reduce((a, d) => a + d.amount, 0),
      },
    };
    console.log('GroupAmountsDiagnostics:', JSON.stringify(payload, null, 2));
  }


  async ngOnInit(): Promise<void> {
    // Detectar rol admin
    try {
      const rolesA = (this.keycloak?.getUserRoles ? this.keycloak.getUserRoles() : []) as string[];
      const rolesB = (this.keycloak?.getUserRoles ? this.keycloak.getUserRoles(true) : []) as string[];
      const all = [...new Set([...(rolesA || []), ...(rolesB || [])].map(r => (r || '').toLowerCase()))];
      let has = all.includes('admin');
      const inst: any = (this.keycloak as any)?.getKeycloakInstance?.();
      if (inst?.hasRealmRole && inst.hasRealmRole('admin')) has = true;
      if (inst?.hasResourceRole && inst.hasResourceRole('admin')) has = true;
      (this as any).isAdmin = !!has;
    } catch { (this as any).isAdmin = false; }
    console.log('OrdersComponent Init');
    // Load local visibility preferences
    try {
      const d = localStorage.getItem(this.DISABLED_STORAGE_KEY);
      if (d) this.disabledMenuProducts = new Set(JSON.parse(d));
    } catch { }
    try {
      const d2 = localStorage.getItem(this.DELETED_STORAGE_KEY);
      if (d2) this.deletedMenuProducts = new Set(JSON.parse(d2));
    } catch { }
    this.isLoading = true;
    this.tableId = this.route.snapshot.paramMap.get("tableUuid");
    const qpCustomer = this.route.snapshot.queryParamMap.get('customer') || '';
    const fromList = this.route.snapshot.queryParamMap.get('fromList') === '1';
    this.orderHashId = this.route.snapshot.paramMap.get("orderHashId") || "";
    console.log({ tableId: this.tableId, orderHashId: this.orderHashId });
    // Cargar selección de agrupación de localStorage
    this.loadGroupSelection();
    // Cargar grupos ya pagados para evitar duplicar pagos parciales
    this.loadPaidGroups();

    // When navigating from list, tableUuid may be missing or placeholder
    if (this.tableId && this.tableId !== 'no-table') {
      const table: any = await this.tableService.getInfoTable(this.tableId).toPromise();
      this.table = table;
    } else {
      // Fallback for view-only contexts
      this.table = { name: 'Sin mesa', type: 'LOCAL' } as any;
    }
    // Normalizar el nombre del cliente desde query params o mesa antes de crear orden
    const initialNameCandidate: any = qpCustomer || (this.table as any)?.customerName || '';
    this.customerName = this.normalizeClientName(initialNameCandidate);

    if (this.tableId) {
      console.log('TableId:', this.tableId);
      // Si estamos creando una nueva orden y no hay nombre de cliente, solicitarlo.
      // Aceptamos vacío como válido (opcional). Solo cancelar si el usuario cierra el diálogo.
      if (!(this.orderHashId?.length > 0) && !this.customerName) {
        try {
          // Apagar el overlay de carga mientras el usuario interactúa con el diálogo
          this.isLoading = false;
          const provided = await this.openClientNameDialog('');
          if (provided === undefined) {
            console.warn('Creación de orden cancelada por el usuario. Volviendo atrás.');
            this.isLoading = false;
            this.location.back();
            return;
          }
          // El diálogo retorna { name, createdAt }; asegurar que asignamos el nombre como string
          this.customerName = this.normalizeClientName(provided?.name || '');
          // Volver a mostrar carga para las llamadas posteriores
          this.isLoading = true;
        } catch {
          // Si hubo un error al abrir el diálogo, no crear la orden
          this.isLoading = false;
          this.location.back();
          return;
        }
      }
      // Determine whether we should create a new order
      const shouldCreate = this.orderHashId.length === 0 && !fromList;
      // If we came from list but we don't have an order id, go back (do not create)
      if (fromList && this.orderHashId.length === 0) {
        console.warn('Navegación desde la lista sin order id. Volviendo para evitar crear una nueva orden.');
        this.location.back();
        this.isLoading = false;
        return;
      }

      Promise.all([
        // Never create a new order if we already have an order id (from list or elsewhere)
        shouldCreate
          ? this.orderService.createOrder({ tableId: this.tableId, clientName: this.customerName }).toPromise()
          : this.orderService.getOrder(this.orderHashId).toPromise(),
        this.categoryService.getCategories().toPromise(),
        this.orderDetailService.getOrderDetailFlows().toPromise(),
        (
          ((this.table?.type || '').toString().toUpperCase() === 'FOODTRUCK')
            ? this.menuProductService.getCatalog(this.tableId, this.orderHashId || null).toPromise()
            : this.menuProductService.getMenuProductsGroupedByCategory().toPromise()
        )
      ])
        .then(([orderResp, categoriesResp, flowsResp, catalogResp]: any[]) => {
          const data = [orderResp, categoriesResp, flowsResp, catalogResp];
          console.log({ data });
          // Some APIs wrap the order under 'order'
          const apiOrder = orderResp && typeof orderResp === 'object' && (orderResp as any).order
            ? (orderResp as any).order
            : orderResp;
          this.order = apiOrder;
          this.order.table = this.table;
          // Sync customer name from order or table when available (normalizado)
          const nameCandidate = (apiOrder as any)?.clientName ?? (apiOrder as any)?.customerName ?? (apiOrder as any)?.table?.clientName ?? this.customerName;
          this.customerName = this.normalizeClientName(nameCandidate);
          // Asegurar asignación robusta del identificador de la orden
          const idCandidate = (
            (apiOrder as any)?.uuid
            || (apiOrder as any)?.hash
            || (apiOrder as any)?.id
            || (apiOrder as any)?.orderUuid
            || (apiOrder as any)?.hashId
          );
          this.orderHashId = (idCandidate ? idCandidate.toString() : '') || this.orderHashId;
          // Sync discount from backend when available (prefer payment.discount if present)
          try {
            const payObj = (apiOrder as any)?.payment;
            if (payObj && typeof (payObj as any).discount !== 'undefined' && (payObj as any).discount !== null) {
              const amt = Number((payObj as any).discount || 0);
              this.discountAmount = isNaN(amt) ? 0 : Math.max(0, amt);
              // Keep any previously set note; payment may not carry notes
            } else {
              const d = (apiOrder as any)?.discount;
              if (d && typeof d === 'object') {
                const amt = Number((d as any).amount || 0);
                this.discountAmount = isNaN(amt) ? 0 : Math.max(0, amt);
                this.discountNote = (
                  (d as any).note || (d as any).reason || (d as any).discountReason || ''
                ).toString();
              } else if ((apiOrder as any)?.discountAmount !== undefined) {
                const amt = Number((apiOrder as any).discountAmount || 0);
                this.discountAmount = isNaN(amt) ? 0 : Math.max(0, amt);
                this.discountNote = (
                  ((apiOrder as any).discountNote)
                  || ((apiOrder as any).discountReason)
                  || ''
                ).toString();
              }
            }
          } catch { }
          // Normalize order status for display and control flags
          const normalized = this.normalizeStatus(apiOrder?.status);
          this.statusOrder = normalized.display;
          this.orderStatusSlug = normalized.slug;
          this.isOrderClosed = normalized.slug === 'CLOSED';
          this.isPaymentDisabled = this.isOrderClosed; // closed orders cannot be paid
          const categoriesSafe = Array.isArray(categoriesResp) ? categoriesResp : [];
          // Mantener el tipo Category intacto sin remapear a una forma incompatible
          this.categories = categoriesSafe as unknown as Category[];
          this.categories$.next(this.categories);

          // Load flows from backend if provided
          const flows = flowsResp?.flows;
          if (flows && typeof flows === 'object') {
            try {
              const normalizeFlowMap = (raw: any): Record<string, string[]> => {
                const out: Record<string, string[]> = {};
                Object.entries(raw || {}).forEach(([from, v]) => {
                  if (Array.isArray(v)) out[from] = v as string[];
                  else if (v && typeof v === 'object' && Array.isArray((v as any).transitions)) out[from] = (v as any).transitions;
                  else out[from] = [];
                });
                return out;
              };

              this.itemStatusFlows = {
                LOCAL: normalizeFlowMap(flows['LOCAL']),
                DELIVERY: normalizeFlowMap(flows['DELIVERY']),
                PICKUP: normalizeFlowMap(flows['PICKUP']),
                FOODTRUCK: normalizeFlowMap(flows['FOODTRUCK']),
              } as any;
            } catch (e) {
              console.warn('Using default item status flows due to parsing error.', e);
              this.itemStatusFlows = this.DEFAULT_ITEM_STATUS_FLOWS;
            }
          } else {
            this.itemStatusFlows = this.DEFAULT_ITEM_STATUS_FLOWS;
          }

          console.log({ categories: this.categories })

          // Catálogo: normalizar la respuesta a grupos para evitar segunda llamada
          try {
            let groups: MenuProductGroup[] = [];
            const isFoodtruck = ((this.table?.type || '').toString().toUpperCase() === 'FOODTRUCK');

            if (isFoodtruck) {
              // Foodtruck: la API puede devolver { categories: [...] } o listas planas
              const categories = Array.isArray((catalogResp as any)?.categories) ? (catalogResp as any).categories : [];
              if (categories.length > 0) {
                groups = categories.map((c: any) => {
                  const id = String(c?.uuid || c?.id || c?.hash || 'unknown');
                  const name = String(c?.name || 'Sin categoría');
                  const items = Array.isArray(c?.items) ? c.items : [];
                  return { categoryId: id, categoryName: name, items } as MenuProductGroup;
                });
              } else {
                const arr = Array.isArray(catalogResp) ? catalogResp
                  : (Array.isArray((catalogResp as any)?.data) ? (catalogResp as any).data
                    : (Array.isArray((catalogResp as any)?.items) ? (catalogResp as any).items : []));
                const looksGrouped = arr.length > 0 && arr[0]?.category && Array.isArray(arr[0]?.items);
                if (looksGrouped) {
                  groups = arr.map((grp: any) => {
                    const cat = grp.category || {};
                    const id = String(cat?.uuid || cat?.hash || cat?.id || 'unknown');
                    const name = String(cat?.name || 'Sin categoría');
                    const items = Array.isArray(grp.items) ? grp.items : [];
                    return { categoryId: id, categoryName: name, items } as MenuProductGroup;
                  });
                } else {
                  const m = new Map<string, MenuProductGroup>();
                  (arr as any[]).forEach((p: any) => {
                    const key = String(
                      p?.categoryUuid || p?.categoryHash || p?.categoryId || p?.category?.uuid || p?.category?.hash || p?.category?.id || 'unknown'
                    );
                    const name = String(p?.categoryName || p?.category?.name || 'Sin categoría');
                    if (!m.has(key)) m.set(key, { categoryId: key, categoryName: name, items: [] });
                    m.get(key)!.items.push(p);
                  });
                  groups = Array.from(m.values());
                }
              }
            } else {
              // No Foodtruck: ya agrupado o lista plana estándar
              if (Array.isArray(catalogResp)) {
                const looksGrouped = catalogResp.length > 0 && (catalogResp as any)[0]?.category && Array.isArray((catalogResp as any)[0]?.items);
                if (looksGrouped) {
                  groups = (catalogResp as any[]).map((grp: any) => {
                    const cat = grp.category || {};
                    const id = String(cat?.uuid || cat?.hash || cat?.id || 'unknown');
                    const name = String(cat?.name || 'Sin categoría');
                    const items = Array.isArray(grp.items) ? grp.items : [];
                    return { categoryId: id, categoryName: name, items } as MenuProductGroup;
                  });
                } else {
                  const m = new Map<string, MenuProductGroup>();
                  (catalogResp as any[]).forEach((p: any) => {
                    const key = String(
                      p?.categoryUuid || p?.categoryHash || p?.categoryId || p?.category?.uuid || p?.category?.hash || p?.category?.id || 'unknown'
                    );
                    const name = String(p?.categoryName || p?.category?.name || 'Sin categoría');
                    if (!m.has(key)) m.set(key, { categoryId: key, categoryName: name, items: [] });
                    m.get(key)!.items.push(p);
                  });
                  groups = Array.from(m.values());
                }
              }
            }

            this.catalogGroups = Array.isArray(groups) ? groups : [];
            // Construir índice de grupos y opciones de pago parcial
            this.buildMenuProductGroupIndex();
            this.computeGroupOptions();
            // Abrir por defecto la primera categoría del catálogo embebido
            if ((this.catalogGroups?.length || 0) > 0 && this.openCategories.size === 0) {
              this.openCategories.add(0);
            }
          } catch (e) {
            console.warn('No se pudo procesar el catálogo de la respuesta inicial', e);
          }

          // Normalize order details from API: support menuProducts[], orderDetail[] and items[]
          let itemsToProcessInit: any[] = [];

          if (Array.isArray((apiOrder as any)?.menuProducts)) {
            (apiOrder as any).menuProducts.forEach((mpGroup: any) => {
              if (Array.isArray(mpGroup.items)) {
                mpGroup.items.forEach((item: any) => {
                  if (!item.menuProduct && mpGroup.menuProduct) {
                    item.menuProduct = mpGroup.menuProduct;
                  }
                  itemsToProcessInit.push(item);
                });
              }
            });
          } else {
            // Fallback
            itemsToProcessInit = Array.isArray((apiOrder as any)?.orderDetail) && (apiOrder as any)?.orderDetail?.length
              ? (apiOrder as any).orderDetail
              : (Array.isArray((apiOrder as any)?.items) ? (apiOrder as any).items : []);
          }

          console.log('Normalized order details from API:', itemsToProcessInit);
          // Build orderList from the normalized rawDetails
          this.orderList = [];
          (itemsToProcessInit || []).forEach((item: any) => {
            const it = new ItemOrder();
            it.qty = (item?.qty ?? item?.quantity ?? 0);
            it.menuProductUuid = item?.menuProductUuid || item?.productUuid || item?.menuProduct?.uuid || item?.product?.uuid;
            it.name = item?.product?.name || item?.menuProduct?.name || 'Producto';
            it.price = item?.price ?? item?.product?.sellPrice ?? item?.menuProduct?.sellPrice ?? 0;
            it.description = item?.product?.description || item?.menuProduct?.description || '';
            it.orderUuid = this.orderHashId;
            it.uuid = (item?.uuid || item?.orderDetailUuid || item?.detailUuid || item?.id || '');
            it.itemStatusSlug = item?.itemStatusSlug || 'pending';
            it.createdAt = item?.createdAt || item?.created_at || item?.createdDate || item?.created || item?.timestamp || undefined;
            if (item.menuProductOption) {
              it.menuProductOption = item.menuProductOption;
              it.menuProductOptionUuid = item.menuProductOptionUuid;
            }
            this.orderList.push(it);
          });
          // Sincronizar nombres y estructura de grupos desde backend (si existen)
          this.syncSavedGroupsFromOrder();
          // Sort and compute totals
          this.sortOrderListByCreated();
          this.recomputeTotals();
          this.updateFlowFlagsAndPayment();
        }).finally(async () => {
          // Solo cargar catálogo agrupado si aún no se obtuvo
          if (!Array.isArray(this.catalogGroups) || this.catalogGroups.length === 0) {
            try {
              const groups = await (
                ((this.table?.type || '').toString().toUpperCase() === 'FOODTRUCK')
                  ? this.menuProductService.getCatalog(this.tableId, this.orderHashId || null).toPromise()
                  : this.menuProductService.getMenuProductsGroupedByCategory().toPromise()
              );
              const isFoodtruck = ((this.table?.type || '').toString().toUpperCase() === 'FOODTRUCK');
              let normalizedGroups: MenuProductGroup[] = [];
              if (isFoodtruck) {
                const categories = Array.isArray((groups as any)?.categories) ? (groups as any).categories : [];
                if (categories.length > 0) {
                  normalizedGroups = categories.map((c: any) => {
                    const id = String(c?.uuid || c?.id || c?.hash || 'unknown');
                    const name = String(c?.name || 'Sin categoría');
                    const items = Array.isArray(c?.items) ? c.items : [];
                    return { categoryId: id, categoryName: name, items } as MenuProductGroup;
                  });
                } else {
                  const arr = Array.isArray(groups) ? (groups as any[])
                    : (Array.isArray((groups as any)?.data) ? (groups as any).data
                      : (Array.isArray((groups as any)?.items) ? (groups as any).items : []));
                  const looksGrouped = arr.length > 0 && arr[0]?.category && Array.isArray(arr[0]?.items);
                  if (looksGrouped) {
                    normalizedGroups = arr.map((grp: any) => {
                      const cat = grp.category || {};
                      const id = String(cat?.uuid || cat?.hash || cat?.id || 'unknown');
                      const name = String(cat?.name || 'Sin categoría');
                      const items = Array.isArray(grp.items) ? grp.items : [];
                      return { categoryId: id, categoryName: name, items } as MenuProductGroup;
                    });
                  } else {
                    const m = new Map<string, MenuProductGroup>();
                    (arr as any[]).forEach((p: any) => {
                      const key = String(
                        p?.categoryUuid || p?.categoryHash || p?.categoryId || p?.category?.uuid || p?.category?.hash || p?.category?.id || 'unknown'
                      );
                      const name = String(p?.categoryName || p?.category?.name || 'Sin categoría');
                      if (!m.has(key)) m.set(key, { categoryId: key, categoryName: name, items: [] });
                      m.get(key)!.items.push(p);
                    });
                    normalizedGroups = Array.from(m.values());
                  }
                }
              } else {
                if (Array.isArray(groups)) {
                  const looksGrouped = groups.length > 0 && (groups as any)[0]?.category && Array.isArray((groups as any)[0]?.items);
                  const looksOurModel = groups.length > 0 && ((groups as any)[0]?.categoryId || (groups as any)[0]?.categoryName) && Array.isArray((groups as any)[0]?.items);
                  if (looksGrouped) {
                    normalizedGroups = (groups as any[]).map((grp: any) => {
                      const cat = grp.category || {};
                      const id = String(cat?.uuid || cat?.hash || cat?.id || 'unknown');
                      const name = String(cat?.name || 'Sin categoría');
                      const items = Array.isArray(grp.items) ? grp.items : [];
                      return { categoryId: id, categoryName: name, items } as MenuProductGroup;
                    });
                  } else if (looksOurModel) {
                    normalizedGroups = (groups as any as MenuProductGroup[]);
                  } else {
                    const m = new Map<string, MenuProductGroup>();
                    (groups as any[]).forEach((p: any) => {
                      const key = String(
                        p?.categoryUuid || p?.categoryHash || p?.categoryId || p?.category?.uuid || p?.category?.hash || p?.category?.id || 'unknown'
                      );
                      const name = String(p?.categoryName || p?.category?.name || 'Sin categoría');
                      if (!m.has(key)) m.set(key, { categoryId: key, categoryName: name, items: [] });
                      m.get(key)!.items.push(p);
                    });
                    normalizedGroups = Array.from(m.values());
                  }
                }
              }
              this.catalogGroups = Array.isArray(normalizedGroups) ? normalizedGroups : [];
              // Construir índice de grupos y opciones de pago parcial
              this.buildMenuProductGroupIndex();
              this.computeGroupOptions();
              // Abrir por defecto la primera categoría del catálogo embebido
              if ((this.catalogGroups?.length || 0) > 0 && this.openCategories.size === 0) {
                this.openCategories.add(0);
              }
            } catch (e) {
              console.warn('No se pudo cargar el catálogo agrupado', e);
            }
          }
          this.isLoading = false
        })
    }
  }

  private normalizeClientName(raw: any): string {
    if (raw === null || raw === undefined) return '';
    if (typeof raw === 'string') return raw.trim();
    if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
    if (typeof raw === 'object') {
      const o: any = raw;
      const composed = [o.firstName, o.lastName].filter(Boolean).join(' ').trim();
      if (composed) return composed;
      const candidates = [o.name, o.fullName, o.displayName, o.title, o.customerName, o.clientName, o.value]
        .filter((v: any) => typeof v === 'string' && v.trim().length);
      if (candidates.length) return candidates[0].trim();
    }
    return '';
  }

  // Abre el modal para capturar/editar el nombre del cliente
  private openClientNameDialog(initial: string): Promise<{ name: string; createdAt?: Date } | undefined> {
    const initialCreated = (
      (this.order?.createdAt)
      || (this.order?.created_at)
      || (this.order?.createdDate)
      || undefined
    );
    const dialogRef = this.dialog.open(ClientNameDialogComponent, {
      width: '360px',
      panelClass: 'client-name-dialog-panel',
      data: { name: initial || '', isAdmin: (this as any).isAdmin, createdAt: initialCreated, orderUuid: this.orderHashId }
    });
    return dialogRef.afterClosed().toPromise().then((val: any) => {
      // undefined -> cancelación
      if (!val || typeof val !== 'object') return undefined;
      // No convertir a string aquí; dejar que normalizeClientName maneje objetos
      const name = val.name ?? '';
      const createdAt = val.createdAt ? new Date(val.createdAt) : undefined;
      return { name, createdAt };
    });
  }

  async loadOrderDetails(): Promise<void> {
    if (!this.orderHashId) return;
    try {
      this.isLoading = true;
      const response = await this.orderService.getOrder(this.orderHashId).toPromise();
      const orderResp = response && typeof response === 'object' ? response : null;
      if (!orderResp) {
        console.error('Invalid order response:', response);
        return;
      }

      // Normalize status and construct local order
      const orderStatus = (orderResp as any).status;
      const statusObj = orderStatus && typeof orderStatus === 'object'
        ? (Array.isArray(orderStatus) ? orderStatus[0] : orderStatus)
        : {};
      this.order = {
        ...(orderResp as object),
        table: this.table,
        status: statusObj,
        orderDetail: Array.isArray((orderResp as any).orderDetail)
          ? (orderResp as any).orderDetail
          : (Array.isArray((orderResp as any).items) ? (orderResp as any).items : [])
      };

      // Sync discount fields (prefer payment.discount when available)
      try {
        const payObj = (orderResp as any)?.payment;
        if (payObj && typeof (payObj as any).discount !== 'undefined' && (payObj as any).discount !== null) {
          const amt = Number((payObj as any).discount || 0);
          this.discountAmount = isNaN(amt) ? 0 : Math.max(0, amt);
        } else {
          const d = (orderResp as any)?.discount;
          if (d && typeof d === 'object') {
            const amt = Number((d as any).amount || 0);
            this.discountAmount = isNaN(amt) ? 0 : Math.max(0, amt);
            this.discountNote = ((d as any).note || '').toString();
          } else if ((orderResp as any)?.discountAmount !== undefined) {
            const amt = Number((orderResp as any).discountAmount || 0);
            this.discountAmount = isNaN(amt) ? 0 : Math.max(0, amt);
            this.discountNote = (((orderResp as any).discountNote) || '').toString();
          }
        }
      } catch { }
      const nameCandidate2 = (orderResp as any)?.clientName ?? (orderResp as any)?.customerName ?? (orderResp as any)?.table?.clientName ?? this.customerName;
      this.customerName = this.normalizeClientName(nameCandidate2);

      const normalized = this.normalizeStatus(statusObj);
      this.statusOrder = normalized.display;
      this.orderStatusSlug = normalized.slug;
      this.isOrderClosed = normalized.slug === 'CLOSED';
      this.isPaymentDisabled = this.isOrderClosed;

      // Build orderList from orderDetail or menuProducts
      const rawDetails = Array.isArray(this.order.orderDetail) ? this.order.orderDetail : [];
      let itemsToProcess: any[] = [];

      // Check for new structure with menuProducts
      if (Array.isArray((this.order as any).menuProducts)) {
        (this.order as any).menuProducts.forEach((mpGroup: any) => {
          if (Array.isArray(mpGroup.items)) {
            mpGroup.items.forEach((item: any) => {
              // Ensure we carry over parent menuProduct info if needed, though usually item has what we need
              // or we might need to merge properties.
              // For now, push the item as is, assuming it has the necessary fields or we enrich it.
              if (!item.menuProduct && mpGroup.menuProduct) {
                item.menuProduct = mpGroup.menuProduct; // Enrich if missing
              }
              itemsToProcess.push(item);
            });
          }
        });
      } else {
        // Fallback to old flat structure
        itemsToProcess = rawDetails;
      }

      this.orderList = [];
      itemsToProcess.forEach((item: any) => {
        const it = new ItemOrder();
        // Fallbacks para qty y uuid por variaciones de API
        it.qty = (item.qty ?? item.quantity ?? 0);
        it.menuProductUuid = item.menuProductUuid || item.productUuid || item.menuProduct?.uuid || item.product?.uuid;
        it.name = item.product?.name || item.menuProduct?.name || 'Producto';
        it.price = item.price ?? item.product?.sellPrice ?? item.menuProduct?.sellPrice ?? 0;
        it.description = item.product?.description || item.menuProduct?.description || '';
        it.orderUuid = this.orderHashId;
        it.uuid = (item.uuid || item.orderDetailUuid || item.detailUuid || item.id || '');
        it.itemStatusSlug = item.itemStatusSlug || 'pending';
        it.createdAt = item.createdAt || item.created_at || item.createdDate || item.created || item.timestamp || undefined;
        // Map option data
        if (item.menuProductOption) {
          it.menuProductOption = item.menuProductOption;
          it.menuProductOptionUuid = item.menuProductOptionUuid;
        }
        // Ensure options and snapshots are carried over for per-card totals
        if (Array.isArray(item.options)) {
          (it as any).options = item.options;
        }
        if (Array.isArray(item.optionSnapshots)) {
          (it as any).optionSnapshots = item.optionSnapshots;
        }
        this.orderList.push(it);
      });

      // Order and totals
      this.sortOrderListByCreated();
      this.recomputeTotals();
      this.updateFlowFlagsAndPayment();
      // Sincronizar nombres y estructura de grupos desde backend antes de recomputar totales agrupados
      this.syncSavedGroupsFromOrder();
      // Recalcular total agrupado según selección y lista actual
      this.recomputeGroupedTotal();
    } catch (error) {
      console.error('Error loading order details:', error);
    } finally {
      // Asegurar catálogo únicamente si no está cargado todavía
      if (!Array.isArray(this.catalogGroups) || this.catalogGroups.length === 0) {
        try {
          const groups = await (
            ((this.table?.type || '').toString().toUpperCase() === 'FOODTRUCK')
              ? this.menuProductService.getCatalog(this.tableId, this.orderHashId || null).toPromise()
              : this.menuProductService.getMenuProductsGroupedByCategory().toPromise()
          );
          const isFoodtruck = ((this.table?.type || '').toString().toUpperCase() === 'FOODTRUCK');
          let normalizedGroups: MenuProductGroup[] = [];
          if (isFoodtruck) {
            const categories = Array.isArray((groups as any)?.categories) ? (groups as any).categories : [];
            if (categories.length > 0) {
              normalizedGroups = categories.map((c: any) => {
                const id = String(c?.uuid || c?.id || c?.hash || 'unknown');
                const name = String(c?.name || 'Sin categoría');
                const items = Array.isArray(c?.items) ? c.items : [];
                return { categoryId: id, categoryName: name, items } as MenuProductGroup;
              });
            } else {
              const arr = Array.isArray(groups) ? (groups as any[])
                : (Array.isArray((groups as any)?.data) ? (groups as any).data
                  : (Array.isArray((groups as any)?.items) ? (groups as any).items : []));
              const looksGrouped = arr.length > 0 && arr[0]?.category && Array.isArray(arr[0]?.items);
              if (looksGrouped) {
                normalizedGroups = arr.map((grp: any) => {
                  const cat = grp.category || {};
                  const id = String(cat?.uuid || cat?.hash || cat?.id || 'unknown');
                  const name = String(cat?.name || 'Sin categoría');
                  const items = Array.isArray(grp.items) ? grp.items : [];
                  return { categoryId: id, categoryName: name, items } as MenuProductGroup;
                });
              } else {
                const m = new Map<string, MenuProductGroup>();
                (arr as any[]).forEach((p: any) => {
                  const key = String(
                    p?.categoryUuid || p?.categoryHash || p?.categoryId || p?.category?.uuid || p?.category?.hash || p?.category?.id || 'unknown'
                  );
                  const name = String(p?.categoryName || p?.category?.name || 'Sin categoría');
                  if (!m.has(key)) m.set(key, { categoryId: key, categoryName: name, items: [] });
                  m.get(key)!.items.push(p);
                });
                normalizedGroups = Array.from(m.values());
              }
            }
          } else {
            if (Array.isArray(groups)) {
              const looksGrouped = groups.length > 0 && (groups as any)[0]?.category && Array.isArray((groups as any)[0]?.items);
              const looksOurModel = groups.length > 0 && ((groups as any)[0]?.categoryId || (groups as any)[0]?.categoryName) && Array.isArray((groups as any)[0]?.items);
              if (looksGrouped) {
                normalizedGroups = (groups as any[]).map((grp: any) => {
                  const cat = grp.category || {};
                  const id = String(cat?.uuid || cat?.hash || cat?.id || 'unknown');
                  const name = String(cat?.name || 'Sin categoría');
                  const items = Array.isArray(grp.items) ? grp.items : [];
                  return { categoryId: id, categoryName: name, items } as MenuProductGroup;
                });
              } else if (looksOurModel) {
                normalizedGroups = (groups as any as MenuProductGroup[]);
              } else {
                const m = new Map<string, MenuProductGroup>();
                (groups as any[]).forEach((p: any) => {
                  const key = String(
                    p?.categoryUuid || p?.categoryHash || p?.categoryId || p?.category?.uuid || p?.category?.hash || p?.category?.id || 'unknown'
                  );
                  const name = String(p?.categoryName || p?.category?.name || 'Sin categoría');
                  if (!m.has(key)) m.set(key, { categoryId: key, categoryName: name, items: [] });
                  m.get(key)!.items.push(p);
                });
                normalizedGroups = Array.from(m.values());
              }
            }
          }
          this.catalogGroups = Array.isArray(normalizedGroups) ? normalizedGroups : [];
          // Actualizar índice y opciones de pago parcial
          this.buildMenuProductGroupIndex();
          this.computeGroupOptions();
          // Abrir por defecto la primera categoría del catálogo embebido
          if ((this.catalogGroups?.length || 0) > 0 && this.openCategories.size === 0) {
            this.openCategories.add(0);
          }
        } catch (e) {
          console.warn('No se pudo cargar el catálogo agrupado', e);
        }
      }
      this.isLoading = false;
    }
  }

  // Badge de estado de la orden
  getOrderStatusBadgeClass(slug: string | undefined): string {
    const s = (slug || 'OPEN').toString().toUpperCase();
    switch (s) {
      case 'CLOSED':
        return 'badge rounded-pill bg-success text-white';
      case 'PAYMENT_PENDING':
        return 'badge rounded-pill bg-danger text-white';
      case 'PENDING':
        return 'badge rounded-pill bg-warning text-dark';
      case 'OPEN':
      default:
        return 'badge rounded-pill bg-secondary text-white';
    }
  }

  async onAddItem(itemUuid: string) {
    if (this.isOrderClosed) { console.warn('Order is closed. No actions allowed.'); return; }
    console.log({ orderList: this.orderList });
    const itemOrder = this.orderList.find(({ uuid }) => uuid === itemUuid);
    if (!itemOrder) return;
    const arrayIndex = this.orderList.indexOf(itemOrder);
    console.log('onAddItem:', itemUuid);
    try {
      this.isLoading = true;
      // If item is not pending, create a new order detail instead of increasing qty
      const isPending = (itemOrder.itemStatusSlug || 'pending').toLowerCase() === 'pending';
      const op$ = isPending
        ? this.orderDetailService.updateOrderDetail({
          orderUuid: itemOrder.orderUuid,
          uuid: itemOrder.uuid,
          qty: itemOrder.qty + 1,
        })
        : this.orderDetailService.createOrderDetail({
          orderUuid: (itemOrder.orderUuid || this.orderHashId || ''),
          menuProductUuid: String(itemOrder.menuProductUuid || ''),
          qty: 1,
        });

      op$.toPromise().then((data: any) => {
        console.log({ data, order: this.order, arrayIndex });
        if (isPending) {
          // Regular increase flow
          if (itemOrder) {
            itemOrder.qty = data.qty;
            this.order.totalAmount = data.orderTotalAmount;
            this.orderList[arrayIndex] = itemOrder;
            if (Array.isArray(this.order.orderDetail)) {
              const odIndex = this.order.orderDetail.findIndex((od: any) => od.uuid === itemOrder.uuid);
              if (odIndex > -1) this.order.orderDetail[odIndex].qty = data.qty;
            }
          }
        } else {
          // Created a new order-detail line (pending)
          const newItem = new ItemOrder();
          newItem.qty = data?.qty ?? 1;
          newItem.menuProductUuid = itemOrder.menuProductUuid;
          newItem.name = itemOrder.name;
          newItem.price = itemOrder.price;
          newItem.description = itemOrder.description;
          newItem.orderUuid = itemOrder.orderUuid;
          newItem.uuid = data?.uuid || '';
          newItem.itemStatusSlug = (data?.itemStatusSlug || 'pending');
          this.orderList.push(newItem);
          // mirror in order.orderDetail if available
          if (!this.order.orderDetail) this.order.orderDetail = [];
          this.order.orderDetail.push(data);
        }

        // Recompute totals, order, and flags
        this.order.totalAmount = data?.orderTotalAmount ?? this.orderList.reduce((acc: number, it) => acc + (it.qty * it.price), 0);
        this.sortOrderListByCreated();
        this.updateFlowFlagsAndPayment();
        // Actualizar selección agrupada si corresponde y recomputar total
        if (this.selectedGroupUuids.has(itemUuid)) {
          const item = this.orderList.find(({ uuid }) => uuid === itemUuid);
          if (item && item.qty <= 0) {
            this.selectedGroupUuids.delete(itemUuid);
            this.saveGroupSelection();
          }
        }
        this.recomputeGroupedTotal();
      }).catch((error: any) => {
        console.error({ error });
      }).finally(() => {
        this.isLoading = false
      })
    } catch (error) {
      console.error({ error });
    }
  }

  onRemoveItem(itemUuid: string) {
    if (this.isOrderClosed) { console.warn('Order is closed. No actions allowed.'); return; }
    console.log('onRemoveItem:', itemUuid);
    // const itemOrder = this.orderList.find(({ uuid }) => uuid === productUuid);
    // if (itemOrder) {
    //   itemOrder.qty--;
    //   if (itemOrder.qty <= 0) {
    //     const arrayIndex = this.orderList.indexOf(itemOrder);
    //     this.orderList.splice(arrayIndex, 1);
    //   }
    // }
    const itemOrder = this.orderList.find(({ uuid }) => uuid === itemUuid);
    if (!itemOrder) return;
    try {
      this.isLoading = true;
      const nextQty = (itemOrder.qty || 0) - 1;
      const actionPromise = (nextQty > 0)
        ? this.orderDetailService.updateOrderDetail({
            orderUuid: itemOrder.orderUuid,
            uuid: itemOrder.uuid,
            qty: nextQty,
          }).toPromise()
        : this.orderDetailService.deleteOrderDetailByUuid(itemOrder.uuid).toPromise();

      actionPromise.then((data: any) => {
        console.log({ data });
        if (!itemOrder) return;

        const arrayIndex = this.orderList.indexOf(itemOrder);

        if (nextQty > 0) {
          itemOrder.qty = (data?.qty ?? nextQty);
          // Update orderDetail mirror qty
          if (Array.isArray(this.order.orderDetail)) {
            const odIndex = this.order.orderDetail.findIndex((od: any) => od.uuid === itemOrder.uuid);
            if (odIndex > -1) this.order.orderDetail[odIndex].qty = itemOrder.qty;
          }
        } else {
          // Remove item locally since it was deleted by UUID
          if (arrayIndex > -1) this.orderList.splice(arrayIndex, 1);
          if (Array.isArray(this.order.orderDetail)) {
            const rmIndex = this.order.orderDetail.findIndex((od: any) => od.uuid === itemOrder.uuid);
            if (rmIndex > -1) this.order.orderDetail.splice(rmIndex, 1);
          }
        }

        // Recompute total using backend value if available, else local reduce
        const backendTotal = data?.orderTotalAmount;
        if (typeof backendTotal === 'number') {
          this.order.totalAmount = backendTotal;
        } else {
          this.order.totalAmount = this.orderList.reduce((acc: number, it) => acc + (it.qty * it.price), 0);
        }

        this.updateFlowFlagsAndPayment();
      }).catch((error: any) => {
        console.error({ error });
      }).finally(() => {
        this.isLoading = false;
      })
    } catch (error) {
      console.error({ error });
    }
  }

  async onProductSelected(productUuid: string) {
    console.log('onProductSelected:', productUuid);
    const menuProductsToFilter = this.categories.map(({ menuProducts }) =>
      menuProducts?.find(({ uuid }) => uuid === productUuid))
    if (menuProductsToFilter) {
      const selectedProduct = menuProductsToFilter.filter((menuProduct) => menuProduct !== undefined)[0] as unknown as MenuProduct;
      if (selectedProduct) {
        const itemOrder = new ItemOrder();
        itemOrder.qty = 1;
        itemOrder.menuProductUuid = /*selectedProduct.uuid*/productUuid;
        itemOrder.name = selectedProduct.name;
        itemOrder.price = selectedProduct.sellPrice;
        itemOrder.description = selectedProduct.description;
        itemOrder.orderUuid = this.orderHashId;
        itemOrder.itemStatusSlug = 'pending';
        console.log({ itemOrder })
        this.orderList.push(itemOrder);
        // Ensure UI enables proper actions (Avanzar, editar) for the new item
        this.updateFlowFlagsAndPayment();
        console.log({ itemOrder })
        try {
          this.isLoading = true
          // Backend expects POST /order-detail/{businessUuid} with body { orderUuid, menuProductUuid, qty }
          const response = await this.orderDetailService.createOrderDetail({
            orderUuid: itemOrder.orderUuid,
            menuProductUuid: itemOrder.menuProductUuid as string,
            qty: itemOrder.qty
          }).toPromise()
          console.log({ response })
          this.orderList[this.orderList.length - 1].uuid = response?.uuid || '';
          // debemos agregar uuid del itemOrder a los valores del item de la tabla
          // suponiendo que corresponde al último elemento del arreglo, es ahí donde lo haremos
          // if (this.orderList.length - 1 >= 0) {
          //   this.orderList[this.orderList.length - 1].uuid = response?.uuid || '';
          // }
          if (!this.order.orderDetail) this.order.orderDetail = [];
          this.order.orderDetail.push(response);
          this.order.totalAmount = this.order.orderDetail.reduce((acc: number, item: any) => acc + item.qty * (item.product?.sellPrice || item.menuProduct?.sellPrice), 0);
          // Recompute flow flags after backend confirms creation
          this.updateFlowFlagsAndPayment();
        }
        catch (error) {
          console.error({ error })
        } finally {
          this.isLoading = false;
        }
      }
    }
  }

  goToOrderView() {
    this.router.navigate(['/admin/order-list']);
  }

  goToPayment() {
    console.log('Go to Payment:', this.orderList);
    if (this.isOrderClosed) { console.warn('Order is closed. Payment disabled.'); return; }
    if (!this.orderList || this.orderList.length === 0) {
      console.warn('No hay ítems en la orden. No se puede proceder al pago.');
      return;
    }
    // Desacoplado de la selección de grupo: pagar siempre el total de la orden
    const chosenItems = this.orderList;
    const totalAmt = this.totalAfterDiscount;
    const dNote = (this.discountNote || '').toString();
    const dialogRef = this.dialog.open(PaymentDialog, {
      data: {
        orderList: chosenItems,
        totalAmount: totalAmt,
        discountAmount: Math.max(0, Math.floor(Number(this.discountAmount || 0))),
        discountNote: dNote
      }
    });
    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (result?.success === true) {
        const isFoodtruck = (this.table?.type || '').toString().toUpperCase() === 'FOODTRUCK';
        if (isFoodtruck) {
          // Para FOODTRUCK: avanzar ítems pendientes al primer estado configurado en el flujo (excluyendo 'canceled')
          const pendingTransitions: string[] =
            this.itemStatusFlows?.['FOODTRUCK']?.['pending'] ||
            this.DEFAULT_ITEM_STATUS_FLOWS['FOODTRUCK']['pending'] ||
            [];
          const nextStatus = pendingTransitions.find(s => s !== 'canceled');
          if (nextStatus) {
            const pendingUuids = this.orderList
              .filter(it => (it.itemStatusSlug || 'pending').toLowerCase() === 'pending')
              .map(it => it.uuid)
              .filter(Boolean);
            const orderUuid = this.orderList[0]?.orderUuid || '';
            if (pendingUuids.length > 0 && orderUuid) {
              try {
                const resp = await this.orderDetailService.processItems(orderUuid, pendingUuids, nextStatus, true).toPromise();
                const items: any[] = Array.isArray(resp) ? resp : resp?.items ? resp.items : [resp];
                const warnings = items.filter((it: any) => it?.warning && it?.warning_message);
                if (warnings.length > 0) {
                  this.toast.warning(warnings[0].warning_message, 'toast-top-right');
                }
              } catch (e) {
                console.error('[FOODTRUCK] Error advancing items after payment', e);
              }
            }
          }
        }
        await this.loadOrderDetails();
      }
    });
  }

  goToPartialPayment() {
    try {
      if (this.isOrderClosed) { console.warn('Order is closed. Payment disabled.'); return; }
      const sel = (this.selectedGroupId || '').toString();
      if (!sel) { console.warn('Seleccione un grupo para pagar parcialmente.'); return; }
      // Evitar pago duplicado del mismo grupo
      if (this.isGroupPaid(sel)) { console.warn('Este grupo ya fue pagado. No se puede repetir.'); return; }
      // Determinar ítems según tipo de grupo seleccionado (catálogo o guardado)
      let items: ItemOrder[] = [];
      const saved = (this.savedGroups || []).find(g => g.gid === sel);
      if (saved) {
        const set = new Set(saved.itemUuids || []);
        items = this.getNonCanceledItems(this.orderList).filter(it => set.has(it.uuid));
      } else {
        items = this.getNonCanceledItems(this.orderList).filter(it => {
          const grp = this.getGroupForItem(it);
          return grp?.id === sel;
        });
      }
      if (items.length === 0) { console.warn('No hay ítems válidos en el grupo seleccionado.'); return; }

      const amount = items.reduce((acc, it) => acc + (Number(it.qty) * Number(it.price)), 0);
      const groupName = this.groupOptions.find(g => g.id === sel)?.name || '';

      const dialogRef = this.dialog.open(PaymentDialog, {
        data: {
          orderList: items,
          totalAmount: amount,
          discountAmount: 0,
          discountNote: `Pago parcial grupo: ${groupName}`,
          isPartial: true,
          groupName: groupName
        }
      });
      dialogRef.afterClosed().subscribe((result: any) => {
        try {
          if (result && result.success === true) {
            const uuids = items.map(it => it.uuid);
            const tip = Number(result?.payment?.tipAmount ?? result?.payment?.tip ?? 0) || 0;
            const totalWithTip = Number(result?.payment?.totalWithTip);
            // Determinar método de pago desde la respuesta del dialog
            const methodFromResp = (() => {
              const m = (result?.payment?.method || result?.paymentMethod || '').toString();
              if (m) return m as any;
              const card = Number(result?.payment?.cardAmount ?? 0) || 0;
              const cash = Number(result?.payment?.cashAmount ?? 0) || 0;
              const agreement = Number(result?.payment?.agreementAmount ?? 0) || 0;
              const count = [card > 0, cash > 0, agreement > 0].filter(Boolean).length;
              if (count > 1) return 'mix';
              if (card > 0) return 'card';
              if (cash > 0) return 'cash';
              if (agreement > 0) return 'agreement';
              return '';
            })();
            this.markGroupPaid(sel, amount, uuids, tip, totalWithTip, methodFromResp);
            console.log('Grupo marcado como pagado:', sel);
            // Si el grupo seleccionado es uno guardado, persistir paymentUuid
            const idx = this.savedGroups.findIndex(g => g.gid === sel);
            if (idx >= 0) {
              const pu = (result?.paymentUuid || result?.payment?.uuid || '').toString();
              if (pu) {
                this.savedGroups[idx].paymentUuid = pu;
                this.saveSavedGroups();
              }
            }
            // Refrescar opciones y limpiar selección si el grupo ya no está disponible
            this.computeGroupOptions();
            if (this.isGroupPaid(this.selectedGroupId)) {
              this.selectedGroupId = '';
            }
          }
        } catch { }
      });
    } catch (e) {
      console.error('goToPartialPayment error', e);
    }
  }

  // Eventos del combobox atómico
  onPartialGroupSelected(opt: ComboOption) {
    // Aceptamos sólo selección de grupos guardados
    const val = opt?.value || '';
    if ((this.savedGroups || []).some(g => g.gid === val)) {
      // Fijar selección y entrar en modo edición de grupo para mostrar los ítems seleccionados
      this.startEditGroup(val);
    } else {
      this.selectedGroupId = '';
    }
  }
  onPartialGroupCleared() {
    // Limpiar selección y salir de modo agrupación
    this.selectedGroupId = '';
    this.selectedGroupUuids.clear();
    this.currentNewGroupUuids.clear();
    this.groupingMode = false;
    this.groupEditMode = false;
    this.recomputeGroupedTotal();
  }

  private formatPlainNumber(n: number): string {
    try { return (Number(n) || 0).toLocaleString('es-CL', { maximumFractionDigits: 0 }); }
    catch { return String(Math.round(Number(n) || 0)); }
  }

  private resolvePrinterMeta(orderUuid: string): { mac: string; topic: string; jobId: string; encoding: string } {
    const mac = (this.localStorage.getItem<string>('PRINTER_MAC') || '').toString().trim();
    const topicFromLs = (this.localStorage.getItem<string>('PRINTER_TOPIC') || '').toString().trim();
    const realm = this.config.keycloakRealm || 'store';
    const bizName = (this.bizCtx.getSelectedName() || '').toString().trim();
    const printerIdent = mac || (bizName ? bizName.replace(/\s+/g, '-') : 'Printer-001');
    const topic = topicFromLs || `${realm}/printer/${printerIdent}/cmd`;
    const jobId = `voucher-${orderUuid}`;
    const encoding = 'base64';
    return { mac, topic, jobId, encoding };
  }

  private async resolvePrinterUuid(): Promise<string | undefined> {
    const uuidFromStorage = (this.localStorage.getItem<string>('PRINTER_UUID') || '').toString().trim();
    if (uuidFromStorage) return uuidFromStorage;
    try {
      const printers = await this.printersService.getPrinters().toPromise();
      const list = Array.isArray(printers) ? printers : [];
      const first = list.find(p => (p as any)?.enabled) || list[0];
      return first?.uuid as string | undefined;
    } catch (e) {
      console.warn('No se pudo resolver PRINTER_UUID desde API', e);
      return undefined;
    }
  }

  async printVoucher() {
    const orderUuid = this.orderHashId || this.order?.uuid;
    if (!orderUuid) {
      console.error('No hay orderUuid para imprimir el voucher');
      return;
    }
    const printerUuid = await this.resolvePrinterUuid();
    if (!printerUuid) {
      console.error('No hay PRINTER_UUID configurado o disponible');
      return;
    }

    // Helpers para sanitizar y envolver texto sin acentos y sin cortes
    const colsSetting = Number((this.localStorage.getItem<string>('PRINTER_COLS') || '').toString());
    const MAX_COLS = Number.isFinite(colsSetting) && colsSetting > 0 ? Math.floor(colsSetting) : 32;
    const sanitizeText = (raw: any): string => {
      const s = ((raw ?? '') as any).toString();
      return s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\x20-\x7E]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };
    const wrapText = (raw: any, width: number): string => {
      const text = sanitizeText(raw);
      if (!text) return '';
      const words = text.split(' ');
      const lines: string[] = [];
      let line = '';
      for (let w of words) {
        if (w.length > width) {
          if (line) { lines.push(line); line = ''; }
          for (let i = 0; i < w.length; i += width) {
            lines.push(w.slice(i, i + width));
          }
        } else if ((line ? line.length + 1 : 0) + w.length <= width) {
          line = line ? line + ' ' + w : w;
        } else {
          lines.push(line);
          line = w;
        }
      }
      if (line) lines.push(line);
      return lines.join('\n');
    };

    // Backend formatea el voucher; enviar solo datos mínimos
    const mac = (this.localStorage.getItem<string>('PRINTER_MAC') || '').toString().trim();
    const payload: any = { orderUuid, encoding: 'base64' };
    if (mac) payload.mac = mac;

    this.isLoading = true;
    console.log('[PrintVoucher] API autenticada (payload mínimo):', payload, 'printerUuid:', printerUuid);
    this.orderService.printVoucherViaPrinterApi(printerUuid, payload).toPromise().then((data: any) => {
      console.log('Voucher impreso vía API de impresoras', data);
    }).catch((e: any) => {
      console.error('Error imprimiendo vía API de impresoras', e);
    }).finally(() => {
      this.isLoading = false;
    });
  }

  openVoucherModal() {
    // Establece la orden en el servicio compartido
    this.sharedOrderService.setOrder(this.order);
    console.log('Open Voucher Modal:', this.order);

    const dialogRef = this.dialog.open(VoucherDialogComponent, {
      data: { order: this.order ?? {} }  // Aquí pasamos la order al dialog
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      console.log(`Dialog result: ${result}`);
    });

  }
}

@Component({
  selector: 'payment-dialog',
  templateUrl: 'payment-dialog.html',
  styleUrl: './payment-dialog.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DecimalPipe,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    CheckboxComponent,
    LoadingSpinnerComponent
  ],
})
export class PaymentDialog {
  // Template-bound state
  isLoading = false;
  direction: 'ltr' | 'rtl' = 'ltr';
  paymentForm!: FormGroup;
  orderList: ItemOrder[] = [];
  totalAmount = 0;
  discountAmount = 0;
  discountNote: string = '';
  private stayOnPage = false;
  private createdDate?: string;
  // Parcial payment info
  isPartial: boolean = false;
  groupName: string = '';
  totalAmountToPay = 0;
  tcTdChecked = false;
  efectivoChecked = false;
  tipPercent = 10; // default 10%
  get tipAmount(): number { return Math.round((this.totalAmount || 0) * this.tipPercent) / 100; }
  get totalWithTip(): number { return (this.totalAmount || 0) + (this.paymentForm?.get('tip')?.value ? (this.paymentForm?.get('tipAmount')?.value || 0) : 0); }
  tipEditing = false;

  // Display-only helpers for the requested formula in the dialog
  get suggestedTipBase10(): number { return Math.round((this.totalAmount || 0) * 10 / 100); }
  get finalTotalBase10(): number { return (this.totalAmount || 0) + this.suggestedTipBase10; }

  constructor(@Inject(MAT_DIALOG_DATA) public data: {
    orderList: ItemOrder[],
    totalAmount: number,
    discountAmount?: number,
    discountNote?: string,
    isPartial?: boolean,
    groupName?: string,
    stayOnPage?: boolean,
    tipDefaultEnabled?: boolean,
  },
    private location: Location,
    private paymentService: PaymentService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<PaymentDialog>,
    private toast: NgxToastrService,
    private router: Router) {
    this.orderList = Array.isArray(data.orderList) ? data.orderList : [];
    this.totalAmount = Number(data.totalAmount) || 0;
    this.discountAmount = Math.max(0, Number(data.discountAmount || 0));
    this.discountNote = (data.discountNote || '').toString();
    this.isPartial = !!data.isPartial;
    this.groupName = (data.groupName || '').toString();
    this.stayOnPage = !!(data as any)?.stayOnPage;
    this.createdDate = (data as any)?.createdDate || undefined;
    const tipEnabledDefault = (data as any)?.tipDefaultEnabled ?? true;
    this.paymentForm = fb.group({
      tcTd: [''],
      tcTdAmount: [{ value: '', disabled: true }, Validators.required],
      tcTdVoucher: [{ value: '', disabled: true }],
      cash: [''],
      cashAmount: [''],
      agreement: [''],
      agreementAmount: [{ value: '', disabled: true }],
      transfer: [''],
      transferAmount: [{ value: '', disabled: true }],
      tip: [tipEnabledDefault],
      tipAmount: [0]
    })
    console.log({ orderList: this.orderList, totalAmount: this.totalAmount });
    // Initialize tip amount to 10%
    const initialTip = Math.round((this.totalAmount || 0) * this.tipPercent / 100);
    this.paymentForm.get('tipAmount')?.setValue(initialTip);
    // Auto-ajustar montos cuando cambia propina o su valor
    this.paymentForm.get('tip')?.valueChanges.subscribe((enabled: boolean) => {
      if (!enabled) this.paymentForm.get('tipAmount')?.setValue(0);
      Promise.resolve().then(() => this.adjustSingleMethodAmountToTotal());
    });
    this.paymentForm.get('tipAmount')?.valueChanges.subscribe(() => {
      this.adjustSingleMethodAmountToTotal();
    });
  };

  async submit() {    // Submit Form 
    console.log('Submit Button Click', this.checkPayment());
    if (!this.orderList || this.orderList.length === 0) {
      console.warn('No hay ítems para pagar.');
      return;
    }
    if (!this.checkPayment()) return;
    this.isLoading = true;
    let success = false;
    const selCard = !!this.paymentForm.get('tcTd')?.value;
    const selCash = !!this.paymentForm.get('cash')?.value;
    const selAgreement = !!this.paymentForm.get('agreement')?.value;
    const selTransfer = !!this.paymentForm.get('transfer')?.value;
    const selectedCount = [selCard, selCash, selAgreement, selTransfer].filter(Boolean).length;
    const paymentMethod = selectedCount > 1
      ? 'mix'
      : (selCard ? 'card' : (selCash ? 'cash' : (selAgreement ? 'agreement' : (selTransfer ? 'transferencia' : 'cash'))));

    // Propina: incluir si el toggle "tip" está activado
    const tipEnabled = !!this.paymentForm?.get('tip')?.value;
    const tipAmount = tipEnabled ? (Number(this.paymentForm?.get('tipAmount')?.value) || 0) : 0;
    const payload: any = {
      orderHashId: this.orderList[0].orderUuid,
      paymentMethod,
      cardAmount: Number(this.paymentForm.get('tcTdAmount')?.value) || 0,
      cashAmount: Number(this.paymentForm.get('cashAmount')?.value) || 0,
      agreementAmount: Number(this.paymentForm.get('agreementAmount')?.value) || 0,
      transferAmount: Number(this.paymentForm.get('transferAmount')?.value) || 0,
      voucher: this.paymentForm.get('tcTdVoucher')?.value,
      // nuevos campos de propina
      tip: tipAmount,
      tipAmount: tipAmount,
      tipPercent: this.tipPercent,
      totalWithTip: this.totalWithTip,
      // incluir descuento aplicado en el ticket
      discountAmount: Math.max(0, Number(this.discountAmount || 0)),
      // fecha retroactiva (solo si difiere de hoy; se pasa como YYYY-MM-DD en timezone America/Santiago)
      ...(this.createdDate ? { createdDate: this.createdDate } : {}),
    };
    let paymentResp: any = null;
    this.paymentService.createPayment(payload as any).toPromise().then((data: any) => {
      console.log({ data });
      paymentResp = data;
      this.toast.success('Pago realizado correctamente', 'toast-top-right');
      success = true;
      if (!this.stayOnPage) {
        this.router.navigate(['/admin/order-list']);
      }
    }).catch((error: any) => {
      console.error({ error });
      const msg = (
        (error?.error && (error?.error?.message || error?.error?.error || error?.error?.detail))
        || error?.message || 'No se pudo procesar el pago. Intenta nuevamente.'
      );
      this.toast.error(String(msg), 'toast-top-right');
    }).finally(() => {
      this.isLoading = false
      this.dialogRef.close({ success, payment: paymentResp, paymentUuid: paymentResp?.uuid });
    })
  }

  ngDoCheck() {
    if (document.body.getAttribute('direction') == 'rtl') {
      this.direction = 'rtl';
    } else {
      this.direction = 'ltr';
    }
  }

  toggleTcTd(event: any) {
    console.log('toggleTcTd:', event.target.checked);
    this.tcTdChecked = event.target.checked;
    if (!this.tcTdChecked) {
      this.paymentForm?.get('tcTdAmount')?.disable();
      this.paymentForm?.get('tcTdVoucher')?.disable();
      this.paymentForm?.get('tcTdAmount')?.setValue(0);
    } else {
      this.paymentForm?.get('tcTdAmount')?.enable();
      this.paymentForm?.get('tcTdVoucher')?.enable();
      if (!this.paymentForm?.get('cash')?.value) {
        this.paymentForm?.get('tcTdAmount')?.setValue(this.totalWithTip);
      }
    }
  }

  toggleEfectivo(event: any) {
    console.log('toggleEfectivo:', event.target.checked);
    this.efectivoChecked = event.target.checked;
    if (!this.efectivoChecked) {
      this.paymentForm?.get('cashAmount')?.disable();
      this.paymentForm?.get('cashAmount')?.setValue(0);
    } else {
      this.paymentForm?.get('cashAmount')?.enable();
      console.log('tcTd:', this.paymentForm?.get('tcTd')?.value);
      if (!this.paymentForm?.get('tcTd')?.value) {
        this.paymentForm?.get('cashAmount')?.setValue(this.totalWithTip);
      }
    }
  }

  toggleAgreement(event: any) {
    const checked = !!event.target?.checked;
    if (!checked) {
      this.paymentForm?.get('agreementAmount')?.disable();
      this.paymentForm?.get('agreementAmount')?.setValue(0);
    } else {
      this.paymentForm?.get('agreementAmount')?.enable();
      if (!this.paymentForm?.get('tcTd')?.value && !this.paymentForm?.get('cash')?.value) {
        this.paymentForm?.get('agreementAmount')?.setValue(this.totalWithTip);
      }
    }
  }

  toggleTransfer(event: any) {
    const checked = !!event.target?.checked;
    if (!checked) {
      this.paymentForm?.get('transferAmount')?.disable();
      this.paymentForm?.get('transferAmount')?.setValue(0);
    } else {
      this.paymentForm?.get('transferAmount')?.enable();
      // Si es el único método seleccionado, prellenar con total + propina
      if (!this.paymentForm?.get('tcTd')?.value && !this.paymentForm?.get('cash')?.value && !this.paymentForm?.get('agreement')?.value) {
        this.paymentForm?.get('transferAmount')?.setValue(this.totalWithTip);
      }
    }
  }

  // Tip UI handlers
  toggleTipEdit() {
    this.tipEditing = !this.tipEditing;
  }

  onTipAmountInput() {
    const amt = Number(this.paymentForm?.get('tipAmount')?.value) || 0;
    const base = this.totalAmount || 0;
    this.tipPercent = base > 0 ? Math.round((amt / base) * 100) : 0;
    this.adjustSingleMethodAmountToTotal();
  }

  checkPayment(): boolean {
    this.totalAmountToPay = 0;
    if (this.paymentForm?.get('tcTd')?.value) {
      this.totalAmountToPay += this.paymentForm?.get('tcTdAmount')?.value;
      // if (this.paymentForm?.get('tcTdVoucher')?.value.length == 0) {
      //   return false;
      // }
    }
    if (this.paymentForm?.get('cash')?.value) {
      this.totalAmountToPay += this.paymentForm?.get('cashAmount')?.value;
    }
    if (this.paymentForm?.get('agreement')?.value) {
      this.totalAmountToPay += this.paymentForm?.get('agreementAmount')?.value;
    }
    if (this.paymentForm?.get('transfer')?.value) {
      this.totalAmountToPay += this.paymentForm?.get('transferAmount')?.value;
    }
    // Include tip if enabled
    const tipEnabled = !!this.paymentForm?.get('tip')?.value;
    const tipAmt = tipEnabled ? (Number(this.paymentForm?.get('tipAmount')?.value) || 0) : 0;
    const expected = (this.totalAmount || 0) + tipAmt;
    if (this.totalAmountToPay != expected) {
      return false;
    }
    return true;
  }

  private adjustSingleMethodAmountToTotal(): void {
    try {
      const methods: Array<{ flag: boolean; amountCtrl: string }> = [
        { flag: !!this.paymentForm?.get('tcTd')?.value, amountCtrl: 'tcTdAmount' },
        { flag: !!this.paymentForm?.get('cash')?.value, amountCtrl: 'cashAmount' },
        { flag: !!this.paymentForm?.get('agreement')?.value, amountCtrl: 'agreementAmount' },
        { flag: !!this.paymentForm?.get('transfer')?.value, amountCtrl: 'transferAmount' },
      ];
      const selected = methods.filter(m => m.flag);
      if (selected.length !== 1) return;
      const ctrl = this.paymentForm.get(selected[0].amountCtrl);
      if (!ctrl) return;
      ctrl.setValue(this.totalWithTip);
    } catch {}
  }

  // Opciones seleccionadas para mostrar en el diálogo de pago
  getItemOptionNames(item: any): string[] {
    try {
      const src: any[] = (item?.optionSnapshots || item?.options || []);
      if (Array.isArray(src) && src.length > 0) {
        return src
          .map((o: any) => (o?.nameSnapshot || o?.name || '').toString().trim())
          .filter((n: string) => !!n);
      }
      const single = (item?.menuProductOption?.name || '').toString().trim();
      return single ? [single] : [];
    } catch {
      return [];
    }
  }

  // Lista colapsada: {count} × {name} para opciones del ítem
  getItemOptionList(item: any): { name: string; count: number }[] {
    try {
      const raw: any[] = item?.optionSnapshots || item?.options || item?.selectedOptions || [];
      const names: string[] = Array.isArray(raw)
        ? raw
          .map((o: any) => (o?.nameSnapshot || o?.optionName || o?.name || o?.menuProductOption?.name || '').toString().trim())
          .filter((n: string) => !!n)
        : [];
      if (names.length === 0) {
        const single = (item?.menuProductOption?.name || '').toString().trim();
        return single ? [{ name: single, count: 1 }] : [];
      }
      const acc = new Map<string, number>();
      for (const n of names) {
        acc.set(n, (acc.get(n) || 0) + 1);
      }
      return Array.from(acc.entries()).map(([name, count]) => ({ name, count }));
    } catch {
      return [];
    }
  }

  // Detalle por opción: nombre, cantidad, precio unitario y subtotal
  getItemOptionBreakdown(item: any): { name: string; count: number; unitPrice: number; subtotal: number }[] {
    try {
      const raw: any[] = item?.optionSnapshots || item?.options || item?.selectedOptions || [];
      const basePrice: number = Number(item?.price ?? item?.menuProduct?.sellPrice ?? item?.product?.sellPrice ?? 0) || 0;
      if (!Array.isArray(raw) || raw.length === 0) {
        const singleName = (item?.menuProductOption?.name || '').toString().trim();
        const delta = Number(item?.menuProductOption?.priceDelta ?? 0) || 0;
        if (!singleName) return [];
        const unitPrice = basePrice + delta;
        return [{ name: singleName, count: 1, unitPrice, subtotal: unitPrice }];
      }
      const acc = new Map<string, { count: number; delta: number }>();
      for (const o of raw) {
        const name = (o?.nameSnapshot || o?.optionName || o?.name || o?.menuProductOption?.name || '').toString().trim();
        if (!name) continue;
        const delta = Number(o?.priceDelta ?? o?.menuProductOption?.priceDelta ?? 0) || 0;
        const prev = acc.get(name);
        if (prev) {
          // Mantener el delta del primer registro, incrementar conteo
          acc.set(name, { count: prev.count + 1, delta: prev.delta });
        } else {
          acc.set(name, { count: 1, delta });
        }
      }
      return Array.from(acc.entries()).map(([name, { count, delta }]) => {
        const unitPrice = basePrice + (Number(delta) || 0);
        const subtotal = unitPrice * count;
        return { name, count, unitPrice, subtotal };
      });
    } catch {
      return [];
    }
  }

  // Cálculo principal por ítem: qty × unitPrice = subtotal
  getItemCalc(item: any): { qty: number; unitPrice: number; subtotal: number } {
    try {
      const optQty = Number(item?.menuProduct?.selectedOptionsCount ?? 0) || 0;
      const optSubtotal = Number(item?.menuProduct?.selectedOptionsSellPriceTotal ?? 0) || 0;
      const unitPriceFromMenu = Number(item?.menuProduct?.sellPrice ?? 0) || 0;
      const unitPriceFallback = Number(item?.price ?? item?.product?.sellPrice ?? 0) || 0;
      const unitPrice = unitPriceFromMenu || unitPriceFallback;

      if (optQty > 0 && optSubtotal > 0) {
        return { qty: optQty, unitPrice, subtotal: optSubtotal };
      }

      const qty = Number(item?.qty ?? 0) || 0;
      const subtotal = qty * unitPrice;
      return { qty, unitPrice, subtotal };
    } catch {
      const qty = Number(item?.qty ?? 0) || 0;
      const unitPrice = Number(item?.price ?? 0) || 0;
      return { qty, unitPrice, subtotal: qty * unitPrice };
    }
  }
}

@Component({
  selector: 'app-voucher-dialog',
  standalone: true,
  templateUrl: './voucher-dialog.html',
  styleUrls: ['./voucher-dialog.css'],
  imports: [
    CommonModule,
    CurrencyPipe,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
  ]
})
export class VoucherDialogComponent implements OnInit {
  order: any;
  isLoading = false;

  constructor(public dialogRef: MatDialogRef<VoucherDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sharedOrderService: SharedOrderService,
    private orderService: OrderService,
    private config: ConfigService,
    private bizCtx: BusinessContextService,
    private printersService: PrintersService,
    private localStorage: LocalStorageService) {
  }

  // Opciones seleccionadas para mostrar en el voucher
  getItemOptionNames(item: any): string[] {
    try {
      const src: any[] = (item?.optionSnapshots || item?.options || []);
      if (Array.isArray(src) && src.length > 0) {
        return src
          .map((o: any) => (o?.nameSnapshot || o?.name || '').toString().trim())
          .filter((n: string) => !!n);
      }
      const single = (item?.menuProductOption?.name || '').toString().trim();
      return single ? [single] : [];
    } catch {
      return [];
    }
  }

  // Status badge helpers
  getStatusBadgeClass(slug: string | undefined): string {
    const s = (slug || 'pending').toLowerCase();
    switch (s) {
      case 'delivered':
        return 'badge rounded-pill bg-success text-white';
      case 'picked':
        return 'badge rounded-pill bg-info text-white';
      case 'sent':
        return 'badge rounded-pill bg-warning text-dark';
      case 'canceled':
        return 'badge rounded-pill bg-danger text-white';
      case 'pending':
      default:
        return 'badge rounded-pill bg-white text-dark border border-secondary';
    }
  }

  getStatusLabel(slug: string | undefined): string {
    const s = (slug || 'pending').toLowerCase();
    switch (s) {
      case 'delivered':
        return 'DELIVERED';
      case 'picked':
        return 'PICKED';
      case 'sent':
        return 'SENT';
      case 'canceled':
        return 'CANCELED';
      case 'pending':
      default:
        return 'PENDING';
    }
  }
  ngOnInit(): void {
    // Obtiene la orden desde el servicio compartido
    this.sharedOrderService.currentOrder.subscribe(order => {
      this.order = order;
    });
  }

  private async resolvePrinterUuid(): Promise<string | undefined> {
    const uuidFromStorage = (this.localStorage.getItem<string>('PRINTER_UUID') || '').toString().trim();
    if (uuidFromStorage) return uuidFromStorage;
    try {
      const printers = await this.printersService.getPrinters().toPromise();
      const list = Array.isArray(printers) ? printers : [];
      const first = list.find(p => (p as any)?.enabled) || list[0];
      return first?.uuid as string | undefined;
    } catch (e) {
      console.warn('VoucherDialog: no se pudo resolver PRINTER_UUID desde API', e);
      return undefined;
    }
  }

  async printVoucher() {
    try {
      const orderUuid = this.order?.uuid || this.data?.order?.uuid;
      if (!orderUuid) {
        console.error('VoucherDialog: no order uuid to print');
        return;
      }
      const printerUuid = await this.resolvePrinterUuid();
      if (!printerUuid) {
        console.error('VoucherDialog: no hay PRINTER_UUID configurado o disponible');
        return;
      }
      // Construir payload nuevo y usar solo gateway RAW
      const mac = (this.localStorage.getItem<string>('PRINTER_MAC') || '').toString().trim();
      // Backend ahora formatea; enviar solo orderUuid + mac opcional
      const payloadMinimal: any = { orderUuid, encoding: 'base64' };
      if (mac) payloadMinimal.mac = mac;

      // Helpers: sanitizar y envolver texto sin acentos y sin cortes
      const colsSetting = Number((this.localStorage.getItem<string>('PRINTER_COLS') || '').toString());
      const MAX_COLS = Number.isFinite(colsSetting) && colsSetting > 0 ? Math.floor(colsSetting) : 32;
      const sanitizeText = (raw: any): string => {
        const s = ((raw ?? '') as any).toString();
        return s
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^\x20-\x7E]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      };
      const wrapText = (raw: any, width: number): string => {
        const text = sanitizeText(raw);
        if (!text) return '';
        const words = text.split(' ');
        const lines: string[] = [];
        let line = '';
        for (let w of words) {
          if (w.length > width) {
            if (line) { lines.push(line); line = ''; }
            for (let i = 0; i < w.length; i += width) {
              lines.push(w.slice(i, i + width));
            }
          } else if ((line ? line.length + 1 : 0) + w.length <= width) {
            line = line ? line + ' ' + w : w;
          } else {
            lines.push(line);
            line = w;
          }
        }
        if (line) lines.push(line);
        return lines.join('\n');
      };

      const items: { qty: number; name: string; price: number; line2?: string }[] = [];
      const details = Array.isArray(this.order?.orderDetail) && this.order?.orderDetail?.length
        ? this.order.orderDetail
        : (Array.isArray((this.order as any)?.items) ? (this.order as any).items : []);
      details
        .filter((item: any) => {
          const s = (item?.itemStatusSlug || item?.status || '').toString().toLowerCase();
          return s !== 'canceled';
        })
        .forEach((item: any) => {
          const name = item?.menuProduct?.name || item?.product?.name || item?.name || 'Producto';
          const price = Number(item?.price ?? item?.menuProduct?.sellPrice ?? item?.product?.sellPrice ?? 0);
          const qty = Math.max(0, Number(item?.qty) || 0);
          const subtotal = qty * price;
          const priceI = Math.round(price);
          const subtotalI = Math.round(subtotal);
          const line2 = wrapText(`${qty} x $${priceI} = $${subtotalI}`, MAX_COLS);
          items.push({ qty, name: wrapText(name, MAX_COLS), price: priceI, line2 });
        });
      const discount = Math.max(0, Math.floor(Number((this.order as any)?.discountAmount || 0)));
      const discountNote = ((this.order as any)?.discountNote || (this.order as any)?.discountReason || '').toString().trim();
      if (discount > 0) {
        const qty = 1;
        const price = -discount;
        const subtotal = qty * price;
        const name = discountNote ? `Descuento (${discountNote})` : 'Descuento';
        const priceIAbs = Math.round(Math.abs(price));
        const subtotalI = Math.round(subtotal);
        const line2 = wrapText(`${qty} x $${priceIAbs} = $${subtotalI}`, MAX_COLS);
        items.push({ qty, name: wrapText(name, MAX_COLS), price, line2 });
      }
      const deliveryInt = Math.max(0, Math.floor(Number((this.order as any)?.deliveryAmount || 0)));
      const deliveryNote = ((this.order as any)?.deliveryNote || '').toString().trim();
      if (deliveryInt > 0) {
        const qty = 1;
        const price = deliveryInt;
        const subtotal = qty * price;
        const name = deliveryNote ? `Delivery (${deliveryNote})` : 'Delivery';
        const priceI = Math.round(price);
        const subtotalI = Math.round(subtotal);
        const line2 = wrapText(`${qty} x $${priceI} = $${subtotalI}`, MAX_COLS);
        items.push({ qty, name: wrapText(name, MAX_COLS), price, line2 });
      }

      const total = items.reduce((sum, it) => sum + (it.qty * it.price), 0);
      const tip = Math.round(total * 0.10);
      const totalWithTip = Math.round(total + tip);
      const header = this.bizCtx?.getSelectedName?.() || 'Padaria do Bairro';
      const baseFooter = wrapText('Obrigado pela preferencia!', MAX_COLS);
      const tail = [
        wrapText(`Propina sugerida (10%): $${tip}`, MAX_COLS),
        wrapText(`Total con propina: $${totalWithTip}`, MAX_COLS),
        '',
        baseFooter,
      ].join('\n');
      // Deja 3 saltos de línea al final de la impresión
      const footerFinal = `${tail}\n\n\n`;
      const payload: any = {
        header: wrapText(header, MAX_COLS),
        items,
        total: Math.round(total),
        footer: footerFinal,
        mac,
        encoding: 'base64',
        currency: '$',
        currencyCode: 'CLP',
        layoutMode: 'two-lines',
      };

      // Siempre usar API autenticada con payload mínimo
      this.isLoading = true;
      console.log('[VoucherDialog] API autenticada (payload mínimo):', payloadMinimal, 'printerUuid:', printerUuid);
      this.orderService.printVoucherViaPrinterApi(printerUuid, payloadMinimal).toPromise().then((data: any) => {
        console.log('VoucherDialog: voucher impreso vía API de impresoras', data);
      }).catch((e: any) => {
        console.error('VoucherDialog: error imprimiendo vía API de impresoras', e);
      }).finally(() => {
        this.isLoading = false;
      });
    } catch (e) {
      console.error('VoucherDialog: excepción al imprimir', e);
    }
  }

  // printVoucher() {
  //   const printContents = document.getElementById('voucher-content')?.innerHTML;

  //   if (!printContents) {
  //     console.error('No content found to print');
  //     return;
  //   }

  //   const popupWin = window.open('', '_blank', 'width=600,height=600,noopener,noreferrer');

  //   if (!popupWin) {
  //     console.error('Popup blocked or not opened');
  //     return;
  //   }

  //   popupWin.document.open();
  //   popupWin.document.write(`
  //     <html>
  //       <head>
  //         <title>Voucher de Pago</title>
  //         <style>
  //           body { font-family: Arial, sans-serif; }
  //           .voucher-container { width: 500px; padding: 10px; border: 1px solid #ddd; }
  //           .total { font-weight: bold; text-align: right; }
  //         </style>
  //       </head>
  //       <body onload="window.print();window.close()">
  //         <div class="voucher-container">
  //           ${printContents}
  //         </div>
  //       </body>
  //     </html>
  //   `);
  //   popupWin.document.close();
  // }

  // (removed) This misplaced printVoucher belonged to OrdersComponent and was placed here by mistake.



  closeDialog(): void {
    this.dialogRef.close();
  }
}
